import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Bot, Sparkles, Brain, BookOpen, Settings2,
  Save, RotateCcw, Play, Eye, History, Loader2, Check, X,
  Zap, MessageSquare, FileText, Users, Target, Lightbulb,
  GitBranch, AlertTriangle, Info, Code, Sliders, Database,
  Search, Filter, Layers, Settings, ChevronDown, ChevronUp, Cpu,
  Plus, Trash2, Edit2, GripVertical, Copy, Bug, Download, Radio,
  Shield, FileCode, ArrowUp, ArrowDown, ArrowLeftRight, Building2, Link2, Clock,
  Layout, Box, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import api from '../services/api';
import UnifiedActionReview, { 
  parseActionBlock, 
  removeActionBlock 
} from '../components/copilot/UnifiedActionReview';
import { ActionEnablementEditor } from '../components/admin/ActionEnablementEditor';
import { UnifiedActionsEditor } from '../components/admin/UnifiedActionsEditor';

// Import unified entity ID processor for admin preview panel
import {
  processEntityIds,
  cleanEntityIdFormatting,
  ENTITY_CONFIG,
} from '../components/copilot/utils/entityIdProcessor';

// Domain icons mapping
const DOMAIN_ICONS = {
  product: FileText,
  goals: Target,
  initiatives: Lightbulb,
  personas: Users,
  epics: GitBranch,
  features: MessageSquare,
  releases: Zap
};

// Category icons and colors
const CATEGORY_CONFIG = {
  item: {
    icon: MessageSquare,
    label: 'Single Entity',
    description: 'Works on one item at a time (feature, epic, etc.)',
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-600 dark:text-blue-400'
  },
  collection: {
    icon: Layers,
    label: 'Collection Scope',
    description: 'Analyzes all items of a type within a product',
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-600 dark:text-purple-400'
  },
  ideation: {
    icon: Sparkles,
    label: 'Ideation Co-Pilots',
    description: 'Stack ranking summaries and prioritization insights',
    color: 'indigo',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-600 dark:text-indigo-400'
  },
  workflow: {
    icon: GitBranch,
    label: 'Workflows',
    description: 'Dedicated UI experiences, wizards, analyzers',
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    textColor: 'text-amber-600 dark:text-amber-400'
  },
  system: {
    icon: Settings,
    label: 'System',
    description: 'Behind the scenes - users never interact directly',
    color: 'gray',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/30',
    textColor: 'text-gray-600 dark:text-gray-400'
  }
};

// CopyButton component for debug log code boxes
const CopyButton = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = React.useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };
  
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 px-2 text-xs"
      onClick={handleCopy}
    >
      {copied ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3 w-3 mr-1" />
          {label}
        </>
      )}
    </Button>
  );
};

// Agent type icons
const AGENT_ICONS = {
  feature_copilot: MessageSquare,
  features_copilot: Layers,
  epic_copilot: GitBranch,
  epics_copilot: Layers,
  release_copilot: Zap,
  releases_copilot: Layers,
  initiative_copilot: Lightbulb,
  goal_copilot: Target,
  goals_copilot: Layers,
  strategy_copilot: Lightbulb,
  ideation_copilot: Sparkles,
  product_onboarding: FileText,
  feature_assessment: Eye,
  conversation_summarizer: MessageSquare,
  edit_item_handler: Settings2,
  persona_copilot: Users,
  personas_copilot: Layers,
  persona_generator: Users,
  gap_analysis: AlertTriangle
};

// =============================================================================
// TOGGLE DEFINITIONS - Per COPILOT_CONTEXT_TOGGLES.md
// Product is ALWAYS included (root of context), so no toggle needed for it
// =============================================================================

// Universal toggles - available for ALL item types
const UNIVERSAL_TOGGLES = {
  // Product details (Product itself is always included as root)
  include_product_vision: { 
    label: "Product Vision", 
    group: "product",
    description: "The high-level vision statement that guides product direction" 
  },
  include_product_audience: { 
    label: "Product Audience", 
    group: "product",
    description: "Target audience description - who the product is built for" 
  },
  
  // Strategy group
  include_positioning: { 
    label: "Positioning", 
    group: "strategy", 
    description: "Value proposition and key differentiators that set the product apart" 
  },
  include_strategic_pillars: { 
    label: "Strategic Pillars", 
    group: "strategy", 
    description: "Core strategic pillars and north star metric guiding decisions" 
  },
  include_business_model: { 
    label: "Business Model", 
    group: "strategy", 
    description: "Revenue model and pricing strategy for the product" 
  },
  
  // Market group
  include_competitive_positioning: { 
    label: "Competitive Positioning", 
    group: "market",
    description: "Analysis of how the product positions against competitors" 
  },
  include_market_segments: { 
    label: "Market Segments", 
    group: "market", 
    description: "Market segments with TAM/SAM/SOM sizing data" 
  },
  include_key_competitors: { 
    label: "Key Competitors", 
    group: "market",
    description: "Competitor information including feature comparisons and notes" 
  },
  include_market_trends: { 
    label: "Market Trends", 
    group: "market",
    description: "Current market trends and industry analysis" 
  },
  
  // Entity collections
  include_personas: { 
    label: "All Personas", 
    group: "entities", 
    description: "All user personas defined for this product" 
  },
  include_selected_persona: { 
    label: "Selected Persona", 
    group: "entities", 
    description: "Only the persona(s) directly linked to the current item" 
  },
  include_goals: { 
    label: "Goals", 
    group: "entities",
    description: "Product goals with success metrics and target values" 
  },
  include_goal_initiatives: { 
    label: "Goal Initiatives", 
    group: "entities", 
    parent: "include_goals", 
    description: "Initiatives nested under each goal for strategic context" 
  },
  include_releases: { 
    label: "Releases", 
    group: "entities",
    description: "All releases with dates and assigned features" 
  }
};

// Item-relative toggles - depend on anchor item type
const ITEM_RELATIVE_TOGGLES = {
  // Parent context (UP)
  include_goal_parent: { 
    label: "Goal (Parent)", 
    group: "parent", 
    availableFor: ["initiative", "epic", "feature"],
    description: "The parent goal this item rolls up to in the hierarchy" 
  },
  include_initiative_parent: { 
    label: "Initiative (Parent)", 
    group: "parent", 
    availableFor: ["epic", "feature"],
    description: "The parent initiative containing this epic or feature" 
  },
  include_epic_parent: { 
    label: "Epic (Parent)", 
    group: "parent", 
    availableFor: ["feature"],
    description: "The parent epic containing this feature" 
  },
  
  // Child context (DOWN)
  include_initiative_epics: { 
    label: "Initiative Epics", 
    group: "child", 
    availableFor: ["initiative"],
    description: "All epics under this initiative" 
  },
  include_epic_features: { 
    label: "Epic Features", 
    group: "child", 
    availableFor: ["epic"],
    description: "All features under this epic" 
  },
  include_feature_requirements: { 
    label: "Feature Requirements", 
    group: "child", 
    availableFor: ["feature"],
    description: "Acceptance criteria and requirements for this feature" 
  },
  include_release_features: { 
    label: "Release Features", 
    group: "child", 
    availableFor: ["release"],
    description: "All features assigned to this release" 
  },
  
  // Sibling context (HORIZONTAL)
  include_sibling_goals: { 
    label: "Sibling Goals", 
    group: "sibling", 
    availableFor: ["goal"],
    description: "Other goals in the same product for cross-reference" 
  },
  include_sibling_initiatives: { 
    label: "Sibling Initiatives", 
    group: "sibling", 
    availableFor: ["initiative"],
    description: "Other initiatives under the same parent goal" 
  },
  include_sibling_epics: { 
    label: "Sibling Epics", 
    group: "sibling", 
    availableFor: ["epic"],
    description: "Other epics under the same initiative" 
  },
  include_sibling_features: { 
    label: "Sibling Features", 
    group: "sibling", 
    availableFor: ["feature"],
    description: "Other features under the same epic" 
  },
  include_sibling_personas: { 
    label: "Sibling Personas", 
    group: "sibling", 
    availableFor: ["persona"],
    description: "Other personas in the same product" 
  },
  include_sibling_releases: { 
    label: "Sibling Releases", 
    group: "sibling", 
    availableFor: ["release"],
    description: "Other releases in the same product" 
  },
  
  // Special
  include_linked_items: { 
    label: "Linked Items", 
    group: "special", 
    availableFor: ["persona"], 
    description: "Goals, initiatives, epics, and features that reference this persona" 
  },
  
  // Sub-toggles (not standalone)
  include_sibling_requirements: { 
    label: "Include Requirements", 
    group: "sub", 
    parent: "include_sibling_features",
    description: "Include requirements for sibling features" 
  }
};

// =============================================================================
// DEPTH-BASED PRESETS (NEW UNIFIED SYSTEM)
// =============================================================================

const DEPTH_PRESETS = {
  none: {
    label: "None",
    description: "Just the center entity - no additional context",
    depth_config: { upward: "none", outward: "none", downward: "none", linkable: "none", linked: "none", product: "none" }
  },
  minimal: {
    label: "Minimal",
    description: "Direct parent + basic product info",
    depth_config: { upward: "immediate", outward: "none", downward: "none", linkable: "none", linked: "none", product: "basic" }
  },
  standard: {
    label: "Standard",
    description: "Parent + sibling awareness + child counts + linked items",
    depth_config: { upward: "immediate", outward: "titles", downward: "counts", linkable: "none", linked: "titles", product: "basic" }
  },
  full: {
    label: "Full",
    description: "Complete context - full hierarchy, siblings, children, linked, and product",
    depth_config: { upward: "full_chain", outward: "full", downward: "full", linkable: "full", linked: "full", product: "full" }
  }
};

// =============================================================================
// ITEM-TYPE-SPECIFIC PRESETS - Per COPILOT_CONTEXT_TOGGLES.md
// =============================================================================

const ITEM_TYPE_PRESETS = {
  feature: {
    none: { label: "None", description: "Current feature only" },
    minimal: { 
      label: "Minimal", 
      description: "Product, Epic (Parent), Selected Persona",
      config: {
        include_product: true,
        include_epic_parent: true,
        include_selected_persona: true,
        include_personality: true
      }
    },
    standard: {
      label: "Standard",
      description: "+ Sibling Features, Requirements",
      config: {
        include_product: true,
        include_epic_parent: true,
        include_selected_persona: true,
        include_sibling_features: true,
        include_feature_requirements: true,
        include_personality: true
      }
    },
    full: {
      label: "Full",
      description: "+ Initiative, Goal, All Personas, Vision",
      config: {
        include_product: true,
        include_product_vision: true,
        include_epic_parent: true,
        include_initiative_parent: true,
        include_goal_parent: true,
        include_selected_persona: true,
        include_personas: true,
        include_sibling_features: true,
        include_feature_requirements: true,
        include_personality: true
      }
    }
  },
  epic: {
    none: { label: "None", description: "Current epic only" },
    minimal: {
      label: "Minimal",
      description: "Product, Vision, Initiative (Parent)",
      config: {
        include_product: true,
        include_product_vision: true,
        include_initiative_parent: true,
        include_personality: true
      }
    },
    standard: {
      label: "Standard",
      description: "+ Selected Persona, Epic Features, Sibling Epics",
      config: {
        include_product: true,
        include_product_vision: true,
        include_initiative_parent: true,
        include_selected_persona: true,
        include_epic_features: true,
        include_sibling_epics: true,
        include_personality: true
      }
    },
    full: {
      label: "Full",
      description: "+ Goal, All Personas, Goals, Strategy, Releases",
      config: {
        include_product: true,
        include_product_vision: true,
        include_initiative_parent: true,
        include_goal_parent: true,
        include_selected_persona: true,
        include_personas: true,
        include_epic_features: true,
        include_sibling_epics: true,
        include_goals: true,
        include_strategic_pillars: true,
        include_releases: true,
        include_personality: true
      }
    }
  },
  goal: {
    none: { label: "None", description: "Current goal only" },
    minimal: {
      label: "Minimal",
      description: "Product, Product Vision",
      config: {
        include_product: true,
        include_product_vision: true,
        include_personality: true
      }
    },
    standard: {
      label: "Standard",
      description: "+ Personas, Goal Initiatives, Sibling Goals",
      config: {
        include_product: true,
        include_product_vision: true,
        include_personas: true,
        include_goal_initiatives: true,
        include_sibling_goals: true,
        include_personality: true
      }
    },
    full: {
      label: "Full",
      description: "+ Positioning, Strategic Pillars, Market Segments, Competitors",
      config: {
        include_product: true,
        include_product_vision: true,
        include_personas: true,
        include_goal_initiatives: true,
        include_sibling_goals: true,
        include_positioning: true,
        include_strategic_pillars: true,
        include_market_segments: true,
        include_key_competitors: true,
        include_personality: true
      }
    }
  },
  initiative: {
    none: { label: "None", description: "Current initiative only" },
    minimal: {
      label: "Minimal",
      description: "Product, Vision, Goal (Parent)",
      config: {
        include_product: true,
        include_product_vision: true,
        include_goal_parent: true,
        include_personality: true
      }
    },
    standard: {
      label: "Standard",
      description: "+ Selected Persona, Initiative Epics, Sibling Initiatives",
      config: {
        include_product: true,
        include_product_vision: true,
        include_goal_parent: true,
        include_selected_persona: true,
        include_initiative_epics: true,
        include_sibling_initiatives: true,
        include_personality: true
      }
    },
    full: {
      label: "Full",
      description: "+ All Personas, Goals, Strategic Pillars, Market Segments",
      config: {
        include_product: true,
        include_product_vision: true,
        include_goal_parent: true,
        include_selected_persona: true,
        include_personas: true,
        include_initiative_epics: true,
        include_sibling_initiatives: true,
        include_goals: true,
        include_strategic_pillars: true,
        include_market_segments: true,
        include_personality: true
      }
    }
  },
  persona: {
    none: { label: "None", description: "Current persona only" },
    minimal: {
      label: "Minimal",
      description: "Product, Vision, Market Segments",
      config: {
        include_product: true,
        include_product_vision: true,
        include_market_segments: true,
        include_personality: true
      }
    },
    standard: {
      label: "Standard",
      description: "+ Sibling Personas, Linked Items",
      config: {
        include_product: true,
        include_product_vision: true,
        include_market_segments: true,
        include_sibling_personas: true,
        include_linked_items: true,
        include_personality: true
      }
    },
    full: {
      label: "Full",
      description: "+ Audience, Competitive Positioning, Competitors",
      config: {
        include_product: true,
        include_product_vision: true,
        include_product_audience: true,
        include_market_segments: true,
        include_sibling_personas: true,
        include_linked_items: true,
        include_competitive_positioning: true,
        include_key_competitors: true,
        include_personality: true
      }
    }
  },
  release: {
    none: { label: "None", description: "Current release only" },
    minimal: {
      label: "Minimal",
      description: "Product, Release Features",
      config: {
        include_product: true,
        include_release_features: true,
        include_personality: true
      }
    },
    standard: {
      label: "Standard",
      description: "+ Sibling Releases",
      config: {
        include_product: true,
        include_release_features: true,
        include_sibling_releases: true,
        include_personality: true
      }
    },
    full: {
      label: "Full",
      description: "+ Personas, Goals, Product Vision",
      config: {
        include_product: true,
        include_product_vision: true,
        include_release_features: true,
        include_sibling_releases: true,
        include_personas: true,
        include_goals: true,
        include_personality: true
      }
    }
  }
};

// Generic presets (fallback for non-item-specific prompts)
const GENERIC_PRESETS = {
  none: { 
    label: "None", 
    description: "No context injected",
    config: { include_product: false, include_personality: false }
  },
  minimal: { 
    label: "Minimal", 
    description: "Product + direct parent only",
    config: {
      include_product: true,
      include_personality: true
    }
  },
  standard: { 
    label: "Standard", 
    description: "Item + parent + product context",
    config: {
      include_product: true,
      include_product_vision: true,
      include_product_audience: true,
      include_personality: true
    }
  },
  full: { 
    label: "Full", 
    description: "Complete product context: all epics, features, goals, personas, initiatives, releases, strategy, market intelligence, and document summaries",
    isExecutivePattern: true,
    config: {
      include_product: true,
      include_product_vision: true,
      include_product_audience: true,
      include_personas: true,
      include_goals: true,
      include_positioning: true,
      include_strategic_pillars: true,
      include_business_model: true,
      include_competitive_positioning: true,
      include_market_segments: true,
      include_key_competitors: true,
      include_market_trends: true,
      include_releases: true,
      include_personality: true
    }
  },
  custom: {
    label: "Custom",
    description: "Manual toggle configuration",
    config: null
  }
};

// Helper to get presets for a prompt based on its domains
const getPresetsForPrompt = (prompt) => {
  if (!prompt?.domains?.length) return GENERIC_PRESETS;
  
  // Map domain to item type (singular)
  const domainToItemType = {
    features: 'feature',
    epics: 'epic',
    goals: 'goal',
    initiatives: 'initiative',
    personas: 'persona',
    releases: 'release'
  };
  
  // Use first domain's presets
  const itemType = domainToItemType[prompt.domains[0]];
  if (itemType && ITEM_TYPE_PRESETS[itemType]) {
    return { ...ITEM_TYPE_PRESETS[itemType], custom: GENERIC_PRESETS.custom };
  }
  
  return GENERIC_PRESETS;
};

// Helper to get item type from prompt
const getItemTypeFromPrompt = (prompt) => {
  if (!prompt?.domains?.length) return null;
  const domainToItemType = {
    features: 'feature',
    epics: 'epic',
    goals: 'goal',
    initiatives: 'initiative',
    personas: 'persona',
    releases: 'release'
  };
  return domainToItemType[prompt.domains[0]] || null;
};

// Helper to get available toggles for an item type
const getAvailableTogglesForItemType = (itemType) => {
  const toggles = { ...UNIVERSAL_TOGGLES };
  
  Object.entries(ITEM_RELATIVE_TOGGLES).forEach(([key, def]) => {
    if (!def.availableFor || def.availableFor.includes(itemType)) {
      toggles[key] = def;
    }
  });
  
  return toggles;
};

// Legacy CONTEXT_PRESETS for backward compatibility
const CONTEXT_PRESETS = GENERIC_PRESETS;

// =============================================================================
// ENTITY ID LINK RENDERER (Admin Preview Panel)
// =============================================================================
// Uses unified entityIdProcessor but with admin-specific rendering:
// - Shows full entity ID as clickable button (vs icon in co-pilot)
// - Opens entity in new tab (vs modal in co-pilot)
// - Used only in AI Prompts admin preview panel

/**
 * AdminEntityLinkedText - Renders entity IDs as clickable buttons for admin preview
 * 
 * This component uses the unified entityIdProcessor for entity detection
 * but has admin-specific rendering (full ID text, opens in new tab).
 * 
 * NOTE: This is intentionally different from the co-pilot EntityLinkedText
 * which shows icons and opens modals. Admin users need to see the raw IDs.
 * 
 * @param {string} text - Text to process
 * @param {Function} onEntityClick - Callback when entity is clicked (type, id) => void
 * @param {string} className - Optional CSS classes
 */
const EntityLinkedText = ({ text, onEntityClick, className = '' }) => {
  if (!text) return null;
  
  // Use unified processor to clean formatting and find entities
  // Note: We include strip entities here since admin may want to see ALL IDs
  const cleanedText = cleanEntityIdFormatting(text);
  
  // Find all entity IDs (both linkable and strip types for admin visibility)
  const allPrefixes = [
    ...Object.keys(ENTITY_CONFIG.linkable),
    ...Object.keys(ENTITY_CONFIG.strip)
  ];
  const entityPattern = new RegExp(
    `(${allPrefixes.join('|')})_[a-f0-9_]+`,
    'gi'
  );
  
  // Find all matches with positions
  const entities = [];
  let match;
  while ((match = entityPattern.exec(cleanedText)) !== null) {
    const id = match[0];
    const prefix = id.split('_')[0].toLowerCase();
    
    // Get config (check linkable first, then strip)
    const linkableConfig = ENTITY_CONFIG.linkable[prefix];
    const stripConfig = ENTITY_CONFIG.strip[prefix];
    const config = linkableConfig || stripConfig;
    
    if (config) {
      entities.push({
        start: match.index,
        end: match.index + id.length,
        id,
        type: config.type,
        label: config.label || config.type,
        color: config.color || 'text-gray-600 dark:text-gray-400',
        linkable: !!linkableConfig,
      });
    }
  }
  
  // Sort by position
  entities.sort((a, b) => a.start - b.start);
  
  // If no entities found, return plain text
  if (entities.length === 0) {
    return <span className={className}>{cleanedText}</span>;
  }
  
  // Build the result with links
  const parts = [];
  let lastIndex = 0;
  
  entities.forEach((entity, idx) => {
    // Add text before this entity
    if (entity.start > lastIndex) {
      parts.push(
        <span key={`text-${idx}`}>{cleanedText.substring(lastIndex, entity.start)}</span>
      );
    }
    
    // Add the clickable entity button
    // Style differs based on whether entity is linkable
    parts.push(
      <button
        key={`entity-${idx}`}
        onClick={() => entity.linkable && onEntityClick && onEntityClick(entity.type, entity.id)}
        className={cn(
          "font-mono text-xs px-1 py-0.5 rounded border transition-colors",
          entity.linkable 
            ? "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
            : "bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 cursor-default opacity-60",
          entity.linkable ? entity.color : "text-gray-400 dark:text-gray-500"
        )}
        title={entity.linkable ? `View ${entity.label}: ${entity.id}` : `${entity.label} (not linkable)`}
        disabled={!entity.linkable}
      >
        {entity.id}
      </button>
    );
    
    lastIndex = entity.end;
  });
  
  // Add remaining text
  if (lastIndex < cleanedText.length) {
    parts.push(<span key="text-final">{cleanedText.substring(lastIndex)}</span>);
  }
  
  return <span className={className}>{parts}</span>;
};

// Response Contracts - Read-only documentation of expected AI response structures
const RESPONSE_CONTRACTS = {
  markdown: {
    label: "Markdown",
    description: "Free-form markdown text response",
    editable: true,
    schema: null,
    note: "AI responds with formatted text. No structured parsing required."
  },
  text: {
    label: "Plain Text",
    description: "Simple text response without formatting",
    editable: true,
    schema: null,
    note: "AI responds with plain text. No structured parsing required."
  },
  json: {
    label: "JSON",
    description: "Structured JSON response parsed by code",
    editable: false,
    note: "⚠️ Changes to JSON structure require developer coordination - code depends on this format.",
    schemas: {
      gap_analysis: {
        name: "Gap Analysis Response",
        description: "Returns categorized feature analysis",
        schema: {
          missing_features: "Array of {title, description, type, priority, rationale}",
          features_to_edit: "Array of {feature_id, current_title, field, current, suggested, reason}",
          features_to_delete: "Array of {feature_id, current_title, reason}",
          summary: "Brief 1-2 sentence summary"
        }
      },
      product_onboarding: {
        name: "Product Onboarding Response",
        description: "Extracts structured product data from vision",
        schema: {
          name: "Product name (string)",
          high_level_vision: "Vision statement (string)",
          target_audience: "Object with primary/secondary segments",
          extracted_features: "Array of feature objects",
          extracted_metrics: "Array of success metrics"
        }
      },
      persona_generator: {
        name: "Persona Generator Response",
        description: "Returns array of user personas",
        schema: {
          personas: "Array of {name, role, segment_name, goals, pain_points, context}"
        }
      },
      conversation_summarizer: {
        name: "Conversation Summary Response",
        description: "Summarizes AI chat conversation",
        schema: {
          title: "Short descriptive title",
          summary: "2-4 sentence summary",
          key_decisions: "Array of decisions made",
          action_items: "Array of pending actions"
        }
      },
      edit_item_handler: {
        name: "Edit Handler Response",
        description: "Returns field updates for items",
        schema: {
          updates: "Object mapping field names to new values",
          explanation: "Why these changes were suggested"
        }
      }
    }
  },
  json_with_actions: {
    label: "JSON with Actions",
    description: "Structured JSON response with markdown message and typed actions array",
    editable: false,
    note: "⚠️ Response must be valid JSON with 'message' (markdown) and optional 'actions' array. Actions include field names, labels, and IDs.",
    schemas: ["message (markdown string)", "actions (typed array with IDs)"],
    exampleResponse: `{
  "message": "I recommend linking **Sarah** to this epic...\\n\\nHer goals align well.",
  "actions": [
    {
      "type": "APPLY_UPDATES",
      "fields": [
        {
          "field": "persona_ids",
          "label": "Target Personas",
          "operation": "add",
          "value": {"id": "persona_abc123", "name": "Sarah"}
        }
      ]
    }
  ]
}`
  }
};

const AIPromptsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // State
  const [prompts, setPrompts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  
  // Category collapse state
  const [collapsedCategories, setCollapsedCategories] = useState({});
  
  // Edit state
  const [editedPrompt, setEditedPrompt] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Version history
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  
  // Preview - Enhanced
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [testMessage, setTestMessage] = useState("Analyze this and suggest improvements.");
  
  // Preview item selection
  const [previewProducts, setPreviewProducts] = useState([]);
  const [previewSelectedProduct, setPreviewSelectedProduct] = useState('');
  const [previewItems, setPreviewItems] = useState([]);
  const [previewSelectedItemType, setPreviewSelectedItemType] = useState('');
  const [previewSelectedItem, setPreviewSelectedItem] = useState('');
  const [loadingPreviewItems, setLoadingPreviewItems] = useState(false);
  
  // Available models based on API keys
  const [availableModels, setAvailableModels] = useState(null);
  
  // Centralized Debug Logging state
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugModalTab, setDebugModalTab] = useState('settings');
  const [debugSettings, setDebugSettings] = useState({
    enabled: false,
    scope_type: 'all',
    scope_organization_id: null,
    scope_product_id: null,
    enabled_copilots: []
  });
  const [debugSettingsLoading, setDebugSettingsLoading] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugLogsLoading, setDebugLogsLoading] = useState(false);
  const [debugLogsTotal, setDebugLogsTotal] = useState(0);
  const [selectedDebugLog, setSelectedDebugLog] = useState(null);
  const [debugLogFilter, setDebugLogFilter] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [products, setProducts] = useState([]);
  const [showClearLogsConfirm, setShowClearLogsConfirm] = useState(false);
  const [resolvedMetadata, setResolvedMetadata] = useState({}); // Cache for resolved ID -> name mappings
  
  // System Rules state
  const [systemRules, setSystemRules] = useState([]);
  const [selectedSystemRule, setSelectedSystemRule] = useState(null);
  const [editedSystemRule, setEditedSystemRule] = useState(null);
  const [systemRuleVersions, setSystemRuleVersions] = useState([]);
  const [systemRulesLoading, setSystemRulesLoading] = useState(false);
  const [hasSystemRuleChanges, setHasSystemRuleChanges] = useState(false);
  
  // Action & Field Registries for unified actions editor
  const [actionRegistry, setActionRegistry] = useState({});
  const [fieldRegistry, setFieldRegistry] = useState({});
  
  // Load available models
  const loadAvailableModels = async () => {
    try {
      const response = await api.get('/api/admin/ai-prompts/available-models');
      setAvailableModels(response.data);
    } catch (err) {
      console.error('Failed to load available models:', err);
    }
  };
  
  // Load action and field registries for unified actions editor
  const loadRegistries = async () => {
    try {
      const [actionsRes, fieldsRes] = await Promise.all([
        api.get('/api/admin/ai-prompts/action-registry'),
        api.get('/api/admin/ai-prompts/field-registry'),
      ]);
      setActionRegistry(actionsRes.data.actions || {});
      setFieldRegistry(fieldsRes.data.registry || {});
    } catch (err) {
      console.error('Failed to load registries:', err);
    }
  };
  
  // Load domains
  const loadDomains = async () => {
    try {
      const response = await api.get('/api/admin/ai-prompts/domains');
      setDomains(response.data.domains || []);
    } catch (err) {
      console.error('Failed to load domains:', err);
    }
  };
  
  // Load system rules
  const loadSystemRules = async () => {
    setSystemRulesLoading(true);
    try {
      const response = await api.get('/api/admin/system-rules');
      setSystemRules(response.data.rules || []);
    } catch (err) {
      console.error('Failed to load system rules:', err);
    } finally {
      setSystemRulesLoading(false);
    }
  };
  
  // Load system rule details with version history
  const loadSystemRuleDetails = async (ruleId) => {
    try {
      const response = await api.get(`/api/admin/system-rules/${ruleId}`);
      setSelectedSystemRule(response.data.rule);
      setEditedSystemRule(JSON.parse(JSON.stringify(response.data.rule)));
      setSystemRuleVersions(response.data.versions || []);
      setHasSystemRuleChanges(false);
    } catch (err) {
      console.error('Failed to load system rule details:', err);
      toast.error('Failed to load system rule details');
    }
  };
  
  // Save system rule
  const saveSystemRule = async () => {
    if (!editedSystemRule) return;
    
    try {
      await api.put(`/api/admin/system-rules/${editedSystemRule.rule_id}`, {
        content: editedSystemRule.content,
        description: editedSystemRule.description
      });
      
      toast.success('System rule saved');
      setHasSystemRuleChanges(false);
      
      // Reload to get new version
      await loadSystemRuleDetails(editedSystemRule.rule_id);
      await loadSystemRules();
    } catch (err) {
      console.error('Failed to save system rule:', err);
      toast.error(err.response?.data?.detail || 'Failed to save system rule');
    }
  };
  
  // Restore system rule version
  const restoreSystemRuleVersion = async (version) => {
    if (!selectedSystemRule) return;
    
    try {
      await api.post(`/api/admin/system-rules/${selectedSystemRule.rule_id}/restore/${version}`);
      toast.success(`Restored to version ${version}`);
      await loadSystemRuleDetails(selectedSystemRule.rule_id);
      await loadSystemRules();
    } catch (err) {
      console.error('Failed to restore version:', err);
      toast.error('Failed to restore version');
    }
  };
  
  // Toggle system rule active status
  const toggleSystemRule = async (ruleId) => {
    try {
      const response = await api.patch(`/api/admin/system-rules/${ruleId}/toggle`);
      toast.success(response.data.is_active ? 'Rule enabled' : 'Rule disabled');
      await loadSystemRules();
      if (selectedSystemRule?.rule_id === ruleId) {
        await loadSystemRuleDetails(ruleId);
      }
    } catch (err) {
      console.error('Failed to toggle rule:', err);
      toast.error('Failed to toggle rule status');
    }
  };
  
  // Load prompts
  const loadPrompts = useCallback(async () => {
    try {
      const params = {};
      if (selectedDomain && selectedDomain !== 'all') {
        params.domain = selectedDomain;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await api.get('/api/admin/ai-prompts', { params });
      setPrompts(response.data.prompts || []);
      
      // Select prompt from URL or first one
      const promptId = searchParams.get('prompt');
      if (promptId) {
        const found = response.data.prompts.find(p => p.prompt_id === promptId);
        if (found) {
          await loadPromptDetails(promptId);
        }
      }
    } catch (err) {
      console.error('Failed to load AI prompts:', err);
      toast.error('Failed to load AI prompts');
    } finally {
      setLoading(false);
    }
  }, [selectedDomain, searchQuery, searchParams]);
  
  const loadPromptDetails = async (promptId) => {
    try {
      const response = await api.get(`/api/admin/ai-prompts/${promptId}`);
      setSelectedPrompt(response.data);
      setEditedPrompt(JSON.parse(JSON.stringify(response.data)));
      setHasChanges(false);
      setSearchParams({ prompt: promptId, domain: selectedDomain });
    } catch (err) {
      console.error('Failed to load prompt details:', err);
      toast.error('Failed to load prompt details');
    }
  };
  
  const loadVersionHistory = async (promptId) => {
    setLoadingVersions(true);
    try {
      const response = await api.get(`/api/admin/ai-prompts/${promptId}/versions`);
      setVersions(response.data.versions || []);
    } catch (err) {
      console.error('Failed to load version history:', err);
      toast.error('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };
  
  // Load products for preview
  const loadPreviewProducts = async () => {
    try {
      const response = await api.get('/api/admin/ai-prompts/test-items/products');
      setPreviewProducts(response.data.products || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };
  
  // Load items for preview based on product and item type
  const loadPreviewItems = async (productId, itemType) => {
    if (!productId || !itemType) return;
    setLoadingPreviewItems(true);
    try {
      const response = await api.get(`/api/admin/ai-prompts/test-items/${productId}/${itemType}`);
      setPreviewItems(response.data.items || []);
    } catch (err) {
      console.error('Failed to load items:', err);
      setPreviewItems([]);
    } finally {
      setLoadingPreviewItems(false);
    }
  };
  
  // Run full preview
  const handleFullPreview = async () => {
    // For Collection Co-pilots, we need product_id (and optionally parent_id)
    // For Item Co-pilots, we need item_id
    if (!editedPrompt) {
      toast.error('No prompt selected');
      return;
    }
    
    if (editedPrompt.category === 'collection') {
      // Collection Co-pilot preview
      if (!previewSelectedProduct) {
        toast.error('Please select a product');
        return;
      }
      
      setPreviewLoading(true);
      setPreviewResult(null);
      
      try {
        const response = await api.post(`/api/admin/ai-prompts/${editedPrompt.prompt_id}/preview-collection`, {
          product_id: previewSelectedProduct,
          parent_id: previewSelectedItem || null,  // Optional parent context
          collection_type: editedPrompt.collection_type || editedPrompt.domains?.[0],
          user_query: testMessage || 'Analyze this collection and provide insights',
          // Send current edited config so preview reflects unsaved changes
          model_1_config: editedPrompt.model_1_config,
          model_2_config: editedPrompt.model_2_config,
          catalog_config: editedPrompt.catalog_config,
          data_request_options: editedPrompt.data_request_options,
          actions: editedPrompt.actions
        });
        setPreviewResult(response.data);
      } catch (err) {
        console.error('Collection preview failed:', err);
        toast.error(err.response?.data?.detail || 'Preview failed');
      } finally {
        setPreviewLoading(false);
      }
    } else {
      // Item Co-pilot preview (original behavior)
      if (!previewSelectedItem) {
        toast.error('Please select an item to test with');
        return;
      }
      
      setPreviewLoading(true);
      setPreviewResult(null);
      
      try {
        const response = await api.post(`/api/admin/ai-prompts/${editedPrompt.prompt_id}/preview-full`, {
          item_type: previewSelectedItemType,
          item_id: previewSelectedItem,
          test_message: testMessage,
          // Send current edited config so preview reflects unsaved changes (unified schema)
          context_config: editedPrompt.context_config,
          model_2_config: editedPrompt.model_2_config
        });
        setPreviewResult(response.data);
      } catch (err) {
        console.error('Preview failed:', err);
        toast.error(err.response?.data?.detail || 'Preview failed');
      } finally {
        setPreviewLoading(false);
      }
    }
  };
  
  // Get item types based on prompt domains
  const getAvailableItemTypes = () => {
    if (!editedPrompt?.domains) return [];
    
    const domainToItemType = {
      features: { value: 'features', label: 'Features' },
      epics: { value: 'epics', label: 'Epics' },
      goals: { value: 'goals', label: 'Goals' },
      personas: { value: 'personas', label: 'Personas' },
      initiatives: { value: 'initiatives', label: 'Initiatives' },
      releases: { value: 'releases', label: 'Releases' },
      product: { value: 'product', label: 'Product' }
    };
    
    const itemTypes = [];
    editedPrompt.domains.forEach(domain => {
      if (domainToItemType[domain] && !itemTypes.find(t => t.value === domainToItemType[domain].value)) {
        itemTypes.push(domainToItemType[domain]);
      }
    });
    
    return itemTypes;
  };
  
  useEffect(() => {
    loadDomains();
    loadSystemRules();
    loadRegistries();  // Load action & field registries
    // Initialize domain from URL on mount
    const urlDomain = searchParams.get('domain');
    if (urlDomain) {
      setSelectedDomain(urlDomain);
    }
    // Load available models on mount
    loadAvailableModels();
    // Load debug settings on mount to show correct button state
    loadDebugSettings();
  }, []);
  
  useEffect(() => {
    loadPrompts();
  }, [selectedDomain, searchQuery]);
  
  // Track changes
  useEffect(() => {
    if (selectedPrompt && editedPrompt) {
      const changed = JSON.stringify(selectedPrompt) !== JSON.stringify(editedPrompt);
      setHasChanges(changed);
    }
  }, [selectedPrompt, editedPrompt]);
  
  // Track system rule changes
  useEffect(() => {
    if (selectedSystemRule && editedSystemRule) {
      const changed = JSON.stringify(selectedSystemRule) !== JSON.stringify(editedSystemRule);
      setHasSystemRuleChanges(changed);
    }
  }, [selectedSystemRule, editedSystemRule]);
  
  // Resolve metadata names when a debug log is selected
  useEffect(() => {
    if (selectedDebugLog?.metadata) {
      resolveMetadataNames(selectedDebugLog.metadata);
    }
  }, [selectedDebugLog]);
  
  // Group prompts by category
  const groupedPrompts = useMemo(() => {
    const groups = {
      item: [],
      collection: [],
      ideation: [],
      workflow: [],
      system: []
    };
    
    prompts.forEach(prompt => {
      const category = prompt.category || 'system';
      if (groups[category]) {
        groups[category].push(prompt);
      }
    });
    
    return groups;
  }, [prompts]);
  
  // Save changes
  const handleSave = async () => {
    if (!editedPrompt || !hasChanges) return;
    
    setSaving(true);
    try {
      // Build save payload using UNIFIED SCHEMA
      // All co-pilots now use model_1_config (orchestrator) and model_2_config (executor)
      // Instructions (role, task, guidelines) live INSIDE model_2_config
      const savePayload = {
        name: editedPrompt.name,
        description: editedPrompt.description,
        context_preset: editedPrompt.context_preset,
        context_config: editedPrompt.context_config,
        depth_config: editedPrompt.depth_config,
        actions: editedPrompt.actions,
        is_active: editedPrompt.is_active,
        copilot_settings: editedPrompt.copilot_settings,
        // Unified schema - ALL co-pilots use these
        model_1_config: editedPrompt.model_1_config,
        model_2_config: editedPrompt.model_2_config,
        // Action registry fields
        enabled_actions: editedPrompt.enabled_actions,
        field_restrictions: editedPrompt.field_restrictions,
        // Domain-driven configuration (determines available actions/fields)
        domains: editedPrompt.domains,
        // Entity configuration (for action/field mapping) - legacy, domains takes precedence
        entity_type: editedPrompt.entity_type,
        entity_display_name: editedPrompt.entity_display_name,
        unsupported_actions: editedPrompt.unsupported_actions,
        // Workflow Co-Pilot specific fields
        audience_configs: editedPrompt.audience_configs,
        static_actions: editedPrompt.static_actions,
        context_sources: editedPrompt.context_sources,
        change_note: `Updated via Admin UI`
      };
      
      // DEBUG: Log what we're sending
      console.log('[SAVE] audience_configs being sent:', JSON.stringify(savePayload.audience_configs, null, 2));
      
      // Add Collection Co-Pilot specific fields
      if (editedPrompt.category === 'collection') {
        savePayload.catalog_config = editedPrompt.catalog_config;
        savePayload.data_request_options = editedPrompt.data_request_options;
      }
      
      const response = await api.put(`/api/admin/ai-prompts/${editedPrompt.prompt_id}`, savePayload);
      
      // DEBUG: Log what we got back
      console.log('[SAVE] Response audience_configs:', JSON.stringify(response.data?.prompt?.audience_configs, null, 2));
      
      toast.success('Prompt saved successfully');
      await loadPromptDetails(editedPrompt.prompt_id);
      await loadPrompts();
    } catch (err) {
      console.error('Failed to save prompt:', err);
      toast.error(err.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };
  
  // =========================================================================
  // CENTRALIZED DEBUG LOGGING FUNCTIONS
  // =========================================================================
  
  // Load debug logging settings
  const loadDebugSettings = async () => {
    setDebugSettingsLoading(true);
    try {
      const response = await api.get('/api/admin/copilot-debug/settings');
      setDebugSettings(response.data);
      // If there's an org selected, load its products
      if (response.data.scope_organization_id) {
        loadProductsForOrg(response.data.scope_organization_id);
      }
    } catch (err) {
      console.error('Failed to load debug settings:', err);
    } finally {
      setDebugSettingsLoading(false);
    }
  };
  
  // Save debug logging settings
  const saveDebugSettings = async (newSettings) => {
    try {
      await api.put('/api/admin/copilot-debug/settings', newSettings);
      setDebugSettings(newSettings);
      toast.success('Debug settings saved');
    } catch (err) {
      console.error('Failed to save debug settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
  };
  
  // Load debug logs
  const loadDebugLogs = async () => {
    setDebugLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (debugLogFilter.length > 0) {
        params.append('prompt_ids', debugLogFilter.join(','));
      }
      const response = await api.get(`/api/admin/copilot-debug/logs?${params.toString()}`);
      setDebugLogs(response.data.logs || []);
      setDebugLogsTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to load debug logs:', err);
      toast.error('Failed to load debug logs');
    } finally {
      setDebugLogsLoading(false);
    }
  };
  
  // Load debug log detail
  const loadDebugLogDetail = async (logId) => {
    try {
      const response = await api.get(`/api/admin/copilot-debug/log/${logId}`);
      setSelectedDebugLog(response.data);
    } catch (err) {
      console.error('Failed to load debug log detail:', err);
      toast.error('Failed to load log details');
    }
  };
  
  // Clear all debug logs
  const clearAllDebugLogs = async () => {
    try {
      const params = debugLogFilter.length > 0 ? `?prompt_ids=${debugLogFilter.join(',')}` : '';
      const response = await api.delete(`/api/admin/copilot-debug/logs${params}`);
      const deletedCount = response.data?.deleted_count || 0;
      toast.success(`Cleared ${deletedCount} debug log${deletedCount !== 1 ? 's' : ''}`);
      setDebugLogs([]);
      setDebugLogsTotal(0);
      setSelectedDebugLog(null);
      setShowClearLogsConfirm(false);
    } catch (err) {
      console.error('Failed to clear debug logs:', err);
      toast.error(err.response?.data?.detail || 'Failed to clear logs');
    }
  };
  
  // Delete a single debug log
  const deleteSingleLog = async (logId, e) => {
    e.stopPropagation(); // Prevent triggering the card click
    try {
      await api.delete(`/api/admin/copilot-debug/logs/${logId}`);
      // Remove from local state
      setDebugLogs(prev => prev.filter(log => log.log_id !== logId));
      // Clear selection if this was the selected log
      if (selectedDebugLog?.log_id === logId) {
        setSelectedDebugLog(null);
      }
      toast.success('Log deleted');
    } catch (err) {
      console.error('Failed to delete log:', err);
      toast.error('Failed to delete log');
    }
  };
  
  // Export debug logs
  const exportDebugLogs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '500');
      if (debugLogFilter.length > 0) {
        params.append('prompt_ids', debugLogFilter.join(','));
      }
      
      const response = await api.get(`/api/admin/copilot-debug/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `copilot_debug_logs_${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Logs exported');
    } catch (err) {
      console.error('Failed to export logs:', err);
      toast.error('Failed to export logs');
    }
  };
  
  // Load organizations for scope selection
  const loadOrganizations = async () => {
    try {
      // Include test orgs for debug purposes
      const response = await api.get('/api/admin/metrics/organizations?exclude_test=false');
      const orgs = response.data.organizations || [];
      // Sort alphabetically by name
      orgs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setOrganizations(orgs);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };
  
  // Load products for a specific organization
  const loadProductsForOrg = async (orgId) => {
    try {
      if (!orgId) {
        setProducts([]);
        return;
      }
      // Use admin endpoint to get all products for this org
      const response = await api.get(`/api/admin/metrics/organizations/${orgId}`);
      const orgProducts = response.data.products || [];
      // Sort alphabetically by name
      orgProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProducts(orgProducts);
    } catch (err) {
      console.error('Failed to load products for org:', err);
      setProducts([]);
    }
  };
  
  // Resolve metadata IDs to human-readable names
  const resolveMetadataNames = async (metadata) => {
    if (!metadata) return;
    
    const updates = {};
    
    // Resolve organization_id
    if (metadata.organization_id && !resolvedMetadata[metadata.organization_id]) {
      const org = organizations.find(o => o.organization_id === metadata.organization_id);
      if (org) {
        updates[metadata.organization_id] = org.name;
      }
    }
    
    // Resolve user_id
    if (metadata.user_id && !resolvedMetadata[metadata.user_id]) {
      try {
        const response = await api.get(`/api/admin/metrics/users/${metadata.user_id}`);
        if (response.data?.user) {
          updates[metadata.user_id] = response.data.user.email || response.data.user.name || 'Unknown User';
        }
      } catch (err) {
        console.error('Failed to resolve user:', err);
        updates[metadata.user_id] = 'Unknown User';
      }
    }
    
    // Resolve product_id - need to fetch from the org's products
    if (metadata.product_id && !resolvedMetadata[metadata.product_id]) {
      try {
        // Fetch org details which includes products
        if (metadata.organization_id) {
          const response = await api.get(`/api/admin/metrics/organizations/${metadata.organization_id}`);
          const products = response.data.products || [];
          const product = products.find(p => p.product_id === metadata.product_id);
          if (product) {
            updates[metadata.product_id] = product.name;
          } else {
            updates[metadata.product_id] = 'Unknown Product';
          }
        }
      } catch (err) {
        console.error('Failed to resolve product:', err);
        updates[metadata.product_id] = 'Unknown Product';
      }
    }
    
    if (Object.keys(updates).length > 0) {
      setResolvedMetadata(prev => ({ ...prev, ...updates }));
    }
  };
  
  // Open debug modal
  const openDebugModal = async () => {
    setShowDebugModal(true);
    await loadDebugSettings();
    await loadOrganizations();
    // Products loaded when org is selected
    await loadDebugLogs();
  };
  
  // Toggle copilot in enabled list
  const toggleCopilotEnabled = (promptId) => {
    const enabled = new Set(debugSettings.enabled_copilots || []);
    if (enabled.has(promptId)) {
      enabled.delete(promptId);
    } else {
      enabled.add(promptId);
    }
    setDebugSettings({
      ...debugSettings,
      enabled_copilots: Array.from(enabled)
    });
  };
  
  // Select/deselect all copilots
  const toggleAllCopilots = (category) => {
    const categoryPrompts = prompts.filter(p => p.category === category).map(p => p.prompt_id);
    const enabled = new Set(debugSettings.enabled_copilots || []);
    const allEnabled = categoryPrompts.every(id => enabled.has(id));
    
    if (allEnabled) {
      categoryPrompts.forEach(id => enabled.delete(id));
    } else {
      categoryPrompts.forEach(id => enabled.add(id));
    }
    
    setDebugSettings({
      ...debugSettings,
      enabled_copilots: Array.from(enabled)
    });
  };
  
  // Discard changes
  const handleDiscard = () => {
    if (selectedPrompt) {
      setEditedPrompt(JSON.parse(JSON.stringify(selectedPrompt)));
      setHasChanges(false);
    }
  };
  
  // Rollback
  const handleRollback = async (versionNum) => {
    try {
      await api.post(`/api/admin/ai-prompts/${selectedPrompt.prompt_id}/rollback/${versionNum}`);
      toast.success(`Rolled back to version ${versionNum}`);
      setShowVersionModal(false);
      await loadPromptDetails(selectedPrompt.prompt_id);
      await loadPrompts();
    } catch (err) {
      console.error('Failed to rollback:', err);
      toast.error(err.response?.data?.detail || 'Failed to rollback');
    }
  };
  
  // Preview
  const handlePreview = async () => {
    if (!editedPrompt) return;
    
    setPreviewLoading(true);
    setPreviewResult(null);
    
    try {
      const response = await api.post(`/api/admin/ai-prompts/${editedPrompt.prompt_id}/preview`, {
        new_system_prompt: hasChanges ? editedPrompt.system_prompt : null,
        test_message: testMessage,
        sample_context_type: "feature"
      });
      setPreviewResult(response.data);
    } catch (err) {
      console.error('Preview failed:', err);
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Update field helper
  const updateField = (field, value) => {
    setEditedPrompt(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const updateNestedField = (parent, field, value) => {
    setEditedPrompt(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };
  
  // Toggle category collapse
  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  if (loading) {
    return (
      <div className={cn("flex h-screen overflow-hidden", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }
  
  const AgentIcon = selectedPrompt ? (AGENT_ICONS[selectedPrompt.agent_type] || Bot) : Bot;
  
  // Render a single prompt card
  const renderPromptCard = (prompt) => {
    const Icon = AGENT_ICONS[prompt.agent_type] || Bot;
    const isSelected = selectedPrompt?.prompt_id === prompt.prompt_id;
    const categoryConfig = CATEGORY_CONFIG[prompt.category] || CATEGORY_CONFIG.system;
    
    return (
      <button
        key={prompt.prompt_id}
        onClick={() => {
          loadPromptDetails(prompt.prompt_id);
          // Clear system rule selection when selecting a prompt
          setSelectedSystemRule(null);
          setEditedSystemRule(null);
        }}
        className={cn(
          "w-full p-4 rounded-lg text-left transition-all border",
          isSelected 
            ? "bg-blue-500/10 border-blue-500/50 ring-1 ring-blue-500/30" 
            : cn("hover:bg-gray-50 dark:hover:bg-gray-800/50", colors.borderPrimary),
          !prompt.is_active && "opacity-50"
        )}
      >
        <div className="flex items-start gap-3">
          <div className={cn("p-2 rounded-lg", categoryConfig.bgColor)}>
            <Icon className={cn("w-4 h-4", categoryConfig.textColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(
                "font-medium text-sm truncate",
                isSelected ? "text-blue-600 dark:text-blue-400" : colors.textPrimary
              )}>
                {prompt.name}
              </span>
              {!prompt.is_active && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  Inactive
                </Badge>
              )}
            </div>
            <p className={cn("text-xs truncate mt-1", colors.textMuted)}>
              {prompt.description}
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {prompt.domains?.map(domain => (
                <Badge 
                  key={domain} 
                  variant="outline" 
                  className="text-[10px] px-1.5 py-0 capitalize"
                >
                  {domain}
                </Badge>
              ))}
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                v{prompt.version}
              </Badge>
              {prompt.category === 'collection' && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-[10px] px-1.5 py-0",
                    prompt.copilot_settings?.bypass_model_1 
                      ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700"
                      : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700"
                  )}
                >
                  {prompt.copilot_settings?.bypass_model_1 ? 'Single Model' : 'Two Model'}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  };
  
  // Render category section
  const renderCategorySection = (category, promptsList) => {
    if (promptsList.length === 0) return null;
    
    const config = CATEGORY_CONFIG[category];
    const CategoryIcon = config.icon;
    const isCollapsed = collapsedCategories[category];
    
    return (
      <div key={category} className="mb-6">
        <Collapsible open={!isCollapsed} onOpenChange={() => toggleCategory(category)}>
          <CollapsibleTrigger asChild>
            <button className={cn(
              "w-full flex items-center gap-3 p-3 rounded-lg transition-colors",
              "hover:bg-gray-100 dark:hover:bg-gray-800",
              config.bgColor
            )}>
              <div className={cn("p-2 rounded-lg", config.bgColor, config.borderColor, "border")}>
                <CategoryIcon className={cn("w-5 h-5", config.textColor)} />
              </div>
              <div className="flex-1 text-left">
                <h3 className={cn("font-semibold", config.textColor)}>
                  {config.label}
                </h3>
                <p className={cn("text-xs", colors.textMuted)}>
                  {config.description}
                </p>
              </div>
              <Badge variant="outline" className="mr-2">
                {promptsList.length}
              </Badge>
              {isCollapsed ? (
                <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
              ) : (
                <ChevronUp className={cn("w-5 h-5", colors.textMuted)} />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pl-2">
              {promptsList.map(prompt => renderPromptCard(prompt))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };
  
  return (
    <div className={cn("flex h-screen overflow-hidden", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="AI Prompts Configuration" />
        
        <div className="flex-1 flex overflow-hidden">
          {/* Main Content - Prompt Browser */}
          <div className={cn("flex-1 flex flex-col overflow-hidden", (selectedPrompt || selectedSystemRule) ? "w-1/2" : "w-full")}>
            {/* Search and Filter Bar */}
            <div className={cn("p-4 border-b flex items-center gap-4", colors.borderPrimary, colors.bgSecondary)}>
              <div className="flex items-center gap-2">
                <Filter className={cn("w-4 h-4", colors.textMuted)} />
                <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                  <SelectTrigger className={cn("w-[180px]", colors.bgInput, colors.borderPrimary)}>
                    <SelectValue placeholder="Select domain..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Domains</SelectItem>
                    {domains.map(domain => (
                      <SelectItem key={domain.value} value={domain.value}>
                        <div className="flex items-center gap-2">
                          {React.createElement(DOMAIN_ICONS[domain.value] || FileText, { className: "w-4 h-4" })}
                          <span className="capitalize">{domain.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 relative">
                <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", colors.textMuted)} />
                <Input
                  placeholder="Search prompts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("pl-10", colors.bgInput, colors.borderPrimary)}
                />
              </div>
              
              <Badge variant="outline" className="px-3 py-1">
                {prompts.length} prompts
              </Badge>
              
              <Button
                variant={debugSettings.enabled ? "default" : "outline"}
                size="sm"
                onClick={openDebugModal}
                className={cn(
                  "gap-2",
                  debugSettings.enabled && "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                )}
              >
                <div className="relative">
                  <Bug className="w-4 h-4" />
                  {debugSettings.enabled && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  )}
                </div>
                Debug Logs
                {debugSettings.enabled && (
                  <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] bg-white/20 text-white">
                    ON
                  </Badge>
                )}
              </Button>
            </div>
            
            {/* Prompt List by Category */}
            <ScrollArea className="flex-1 p-4">
              {selectedDomain !== 'all' && (
                <div className={cn("mb-4 p-3 rounded-lg flex items-center gap-3", colors.bgSecondary)}>
                  {React.createElement(DOMAIN_ICONS[selectedDomain] || FileText, { 
                    className: cn("w-5 h-5", colors.textMuted) 
                  })}
                  <div>
                    <span className={cn("font-medium capitalize", colors.textPrimary)}>
                      {domains.find(d => d.value === selectedDomain)?.label || selectedDomain}
                    </span>
                    <span className={cn("text-sm ml-2", colors.textMuted)}>
                      {domains.find(d => d.value === selectedDomain)?.description}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedDomain('all')}
                    className="ml-auto"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {/* Category order: system rules, collection, item, ideation, workflow, system */}
              
              {/* System Rules Section */}
              {systemRules.length > 0 && (
                <div className="mb-4">
                  <Collapsible defaultOpen={true}>
                    <CollapsibleTrigger asChild>
                      <button className={cn(
                        "w-full flex items-center justify-between py-2 px-3 rounded-lg transition-colors",
                        colors.bgSecondary, "hover:opacity-80"
                      )}>
                        <div className="flex items-center gap-2">
                          <div className={cn("p-1.5 rounded", "bg-slate-500/20")}>
                            <Shield className="w-4 h-4 text-slate-500" />
                          </div>
                          <span className={cn("font-medium", colors.textPrimary)}>System Rules</span>
                          <Badge variant="outline" className="ml-1 text-xs">
                            {systemRules.length}
                          </Badge>
                        </div>
                        <ChevronDown className={cn("w-5 h-5 transition-transform", colors.textMuted)} />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <p className={cn("text-xs mb-3 mt-2 pl-2", colors.textMuted)}>
                        Universal rules injected into all Co-Pilot prompts. Click to view and edit.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pl-2">
                        {systemRules.map(rule => (
                          <button
                            key={rule.rule_id}
                            onClick={() => {
                              loadSystemRuleDetails(rule.rule_id);
                              setSelectedPrompt(null);
                              setEditedPrompt(null);
                            }}
                            className={cn(
                              "group p-3 rounded-lg border text-left transition-all duration-200",
                              selectedSystemRule?.rule_id === rule.rule_id
                                ? "border-slate-500 bg-slate-50 dark:bg-slate-900/30 ring-1 ring-slate-500"
                                : cn(colors.bgCard, colors.borderPrimary, "hover:border-slate-400 hover:shadow-md")
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "p-1.5 rounded transition-colors",
                                  selectedSystemRule?.rule_id === rule.rule_id
                                    ? "bg-slate-500/20"
                                    : colors.bgTertiary
                                )}>
                                  <FileCode className={cn(
                                    "w-3.5 h-3.5",
                                    selectedSystemRule?.rule_id === rule.rule_id
                                      ? "text-slate-500"
                                      : colors.textMuted
                                  )} />
                                </div>
                                <span className={cn("font-medium text-sm", colors.textPrimary)}>
                                  {rule.name}
                                </span>
                              </div>
                              <Badge 
                                variant={rule.is_active ? "default" : "secondary"}
                                className={cn(
                                  "text-[10px]",
                                  rule.is_active 
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                    : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                                )}
                              >
                                {rule.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                            <p className={cn("text-xs mt-2 line-clamp-2", colors.textMuted)}>
                              {rule.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-[10px]">
                                v{rule.version || 1}
                              </Badge>
                              {rule.applies_to && (
                                <span className={cn("text-[10px]", colors.textMuted)}>
                                  {Array.isArray(rule.applies_to) ? rule.applies_to.join(', ') : rule.applies_to}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}
              
              {renderCategorySection('collection', groupedPrompts.collection)}
              {renderCategorySection('item', groupedPrompts.item)}
              {renderCategorySection('ideation', groupedPrompts.ideation)}
              {renderCategorySection('workflow', groupedPrompts.workflow)}
              {renderCategorySection('system', groupedPrompts.system)}
              
              {prompts.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Brain className={cn("w-16 h-16 mb-4", colors.textMuted)} />
                  <h3 className={cn("font-medium", colors.textPrimary)}>No prompts found</h3>
                  <p className={cn("text-sm mt-1", colors.textMuted)}>
                    {searchQuery ? 'Try adjusting your search' : 'Select a different domain'}
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
          
          {/* Right Panel - System Rule Editor */}
          {selectedSystemRule && editedSystemRule && (
            <div className={cn("w-1/2 flex flex-col overflow-hidden border-l", colors.borderPrimary)}>
              {/* Header */}
              <div className={cn("px-6 py-4 border-b flex items-center justify-between", colors.borderPrimary, colors.bgSecondary)}>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedSystemRule(null);
                      setEditedSystemRule(null);
                    }}
                    className="p-1"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  <div className={cn("p-2 rounded-lg", "bg-slate-100 dark:bg-slate-800")}>
                    <Shield className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <h2 className={cn("font-semibold", colors.textPrimary)}>
                      {editedSystemRule.name}
                    </h2>
                    <p className={cn("text-sm", colors.textMuted)}>
                      System Rule • v{editedSystemRule.version || 1}
                    </p>
                  </div>
                  {!editedSystemRule.is_active && (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                  {hasSystemRuleChanges && (
                    <Badge className="bg-amber-500 text-white">Unsaved</Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {systemRuleVersions.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn("border", colors.borderPrimary)}
                        >
                          <History className="w-4 h-4 mr-2" />
                          History
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className={cn("w-80", colors.bgCard, colors.borderPrimary)} align="end">
                        <div className="space-y-2">
                          <h4 className={cn("font-medium", colors.textPrimary)}>Version History</h4>
                          <ScrollArea className="h-[200px]">
                            {systemRuleVersions.map((version) => (
                              <div
                                key={version.version}
                                className={cn(
                                  "p-2 rounded flex items-center justify-between",
                                  "hover:bg-slate-100 dark:hover:bg-slate-800"
                                )}
                              >
                                <div>
                                  <p className={cn("text-sm font-medium", colors.textPrimary)}>
                                    Version {version.version}
                                  </p>
                                  <p className={cn("text-xs", colors.textMuted)}>
                                    {version.updated_by} • {new Date(version.updated_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => restoreSystemRuleVersion(version.version)}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </ScrollArea>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleSystemRule(editedSystemRule.rule_id)}
                    className={cn("border", colors.borderPrimary)}
                  >
                    {editedSystemRule.is_active ? 'Disable' : 'Enable'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditedSystemRule(JSON.parse(JSON.stringify(selectedSystemRule)));
                      setHasSystemRuleChanges(false);
                    }}
                    disabled={!hasSystemRuleChanges}
                    className={cn("border", colors.borderPrimary)}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                  
                  <Button
                    onClick={saveSystemRule}
                    disabled={!hasSystemRuleChanges}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                </div>
              </div>
              
              {/* Content */}
              <ScrollArea className="flex-1">
                <div className="p-6 space-y-6">
                  {/* Info Card */}
                  <Card className={cn("p-4 border-l-4 border-l-slate-500", colors.bgCard, colors.borderPrimary)}>
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-slate-500 mt-0.5" />
                      <div>
                        <h3 className={cn("font-medium", colors.textPrimary)}>About This Rule</h3>
                        <p className={cn("text-sm mt-1", colors.textMuted)}>
                          {editedSystemRule.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Badge variant="outline" className="text-xs">
                            Category: {editedSystemRule.category}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            Injection: {editedSystemRule.injection_point}
                          </Badge>
                          {(Array.isArray(editedSystemRule.applies_to) ? editedSystemRule.applies_to : []).map(type => (
                            <Badge key={type} variant="outline" className="text-xs capitalize">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                  
                  {/* Rule Content */}
                  <div>
                    <Label className={colors.textSecondary}>Rule Content</Label>
                    <p className={cn("text-xs mb-2", colors.textMuted)}>
                      This text is injected into Co-Pilot prompts. Use {"{{placeholders}}"} for dynamic values.
                    </p>
                    <Textarea
                      value={editedSystemRule.content || ''}
                      onChange={(e) => setEditedSystemRule({
                        ...editedSystemRule,
                        content: e.target.value
                      })}
                      className={cn("min-h-[400px] font-mono text-sm", colors.bgInput, colors.borderPrimary)}
                      placeholder="Enter the rule content..."
                    />
                  </div>
                  
                  {/* Metadata */}
                  <div className={cn("pt-4 border-t text-xs", colors.borderPrimary, colors.textMuted)}>
                    <p>Rule ID: <code className={cn("px-1 rounded", colors.bgTertiary)}>{editedSystemRule.rule_id}</code></p>
                    <p className="mt-1">
                      Last updated: {editedSystemRule.updated_at ? new Date(editedSystemRule.updated_at).toLocaleString() : 'Never'}
                      {editedSystemRule.updated_by && ` by ${editedSystemRule.updated_by}`}
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </div>
          )}
          
          {/* Right Panel - Editor */}
          {selectedPrompt && editedPrompt && (
            <div className={cn("w-1/2 flex flex-col overflow-hidden border-l", colors.borderPrimary)}>
              {/* Header */}
              <div className={cn("px-6 py-4 border-b flex items-center justify-between", colors.borderPrimary, colors.bgSecondary)}>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedPrompt(null);
                      setEditedPrompt(null);
                      setSearchParams({ domain: selectedDomain });
                    }}
                    className="p-1"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  <div className={cn("p-2 rounded-lg", colors.bgTertiary)}>
                    <AgentIcon className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h2 className={cn("font-semibold", colors.textPrimary)}>
                      {editedPrompt.name}
                    </h2>
                    <p className={cn("text-sm", colors.textMuted)}>
                      {editedPrompt.agent_type} • v{editedPrompt.version}
                    </p>
                  </div>
                  {!editedPrompt.is_active && (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                  {hasChanges && (
                    <Badge className="bg-amber-500 text-white">Unsaved</Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      loadVersionHistory(selectedPrompt.prompt_id);
                      setShowVersionModal(true);
                    }}
                    className={cn("border", colors.borderPrimary)}
                  >
                    <History className="w-4 h-4 mr-2" />
                    History
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowPreviewModal(true);
                      loadPreviewProducts();
                      setPreviewResult(null);
                    }}
                    className={cn("border", colors.borderPrimary)}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  {hasChanges && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDiscard}
                        className={cn("border", colors.borderPrimary)}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Discard
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Tabs - Unified for all Co-Pilots */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <div className={cn("px-6 border-b overflow-x-auto", colors.borderPrimary)}>
                  <TabsList className="h-12 bg-transparent">
                    <TabsTrigger value="about" className="data-[state=active]:bg-blue-500/10">
                      <Info className="w-4 h-4 mr-2" />
                      About
                    </TabsTrigger>
                    {/* Workflow-specific tabs */}
                    {editedPrompt.category === 'workflow' && (
                      <>
                        <TabsTrigger value="workflow-actions" className="data-[state=active]:bg-amber-500/10">
                          <Zap className="w-4 h-4 mr-2" />
                          Actions
                        </TabsTrigger>
                        <TabsTrigger value="workflow-context" className="data-[state=active]:bg-amber-500/10">
                          <Database className="w-4 h-4 mr-2" />
                          Context Sources
                        </TabsTrigger>
                      </>
                    )}
                    {/* Standard tabs for non-workflow */}
                    {editedPrompt.category !== 'workflow' && (
                      <TabsTrigger value="context" className="data-[state=active]:bg-blue-500/10">
                        <Sliders className="w-4 h-4 mr-2" />
                        Context
                      </TabsTrigger>
                    )}
                    <TabsTrigger value="model" className="data-[state=active]:bg-blue-500/10">
                      <Cpu className="w-4 h-4 mr-2" />
                      Model
                    </TabsTrigger>
                    <TabsTrigger value="instructions" className="data-[state=active]:bg-blue-500/10">
                      <Code className="w-4 h-4 mr-2" />
                      Instructions
                    </TabsTrigger>
                    {editedPrompt.category !== 'workflow' && (
                      <TabsTrigger value="actions" className="data-[state=active]:bg-blue-500/10">
                        <Zap className="w-4 h-4 mr-2" />
                        Actions ({editedPrompt.enabled_actions?.length || 0})
                      </TabsTrigger>
                    )}
                    <TabsTrigger value="copilot-settings" className="data-[state=active]:bg-blue-500/10">
                      <Settings2 className="w-4 h-4 mr-2" />
                      Settings
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                <ScrollArea className="flex-1">
                  {/* ========================================================= */}
                  {/* UNIFIED CO-PILOT TABS                                     */}
                  {/* ========================================================= */}
                  
                  {/* About Tab */}
                  <TabsContent value="about" className="p-6 space-y-6 mt-0">
                    {/* Row 1: Status | Agent Type | Category */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className={colors.textSecondary}>Status</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <Switch
                            checked={editedPrompt.is_active}
                            onCheckedChange={(v) => updateField('is_active', v)}
                          />
                          <span className={cn("text-sm", editedPrompt.is_active ? "text-green-600" : colors.textMuted)}>
                            {editedPrompt.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div>
                        <Label className={colors.textSecondary}>Agent Type</Label>
                        <div className="mt-1">
                          <code className={cn("px-2 py-1 rounded text-sm", colors.bgTertiary, colors.textMuted)}>
                            {editedPrompt.agent_type}
                          </code>
                        </div>
                      </div>
                      <div>
                        <Label className={colors.textSecondary}>Category</Label>
                        <div className="mt-1">
                          <Badge className={cn(CATEGORY_CONFIG[editedPrompt.category]?.bgColor, CATEGORY_CONFIG[editedPrompt.category]?.textColor)}>
                            {CATEGORY_CONFIG[editedPrompt.category]?.label || editedPrompt.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    {/* Row: Entity Configuration */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={colors.textSecondary}>Entity Type</Label>
                        <p className={cn("text-xs mb-1", colors.textMuted)}>Determines which fields are shown in Actions tab</p>
                        <Select 
                          value={editedPrompt.entity_type || ''} 
                          onValueChange={(v) => updateField('entity_type', v)}
                        >
                          <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select entity type..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="feature">Feature</SelectItem>
                            <SelectItem value="epic">Epic</SelectItem>
                            <SelectItem value="goal">Goal</SelectItem>
                            <SelectItem value="initiative">Initiative</SelectItem>
                            <SelectItem value="persona">Persona</SelectItem>
                            <SelectItem value="product">Product</SelectItem>
                            <SelectItem value="release">Release</SelectItem>
                            <SelectItem value="request">Request</SelectItem>
                            <SelectItem value="kpi">KPI</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className={colors.textSecondary}>Entity Display Name</Label>
                        <p className={cn("text-xs mb-1", colors.textMuted)}>Used in &quot;Not for [Name]&quot; action badges</p>
                        <Input
                          value={editedPrompt.entity_display_name || ''}
                          onChange={(e) => updateField('entity_display_name', e.target.value)}
                          className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                          placeholder={editedPrompt.entity_type || 'e.g., Market, Feature'}
                        />
                      </div>
                    </div>
                    
                    {/* Row 2: Name (span 3) */}
                    <div>
                      <Label className={colors.textSecondary}>Name</Label>
                      <Input
                        value={editedPrompt.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                      />
                    </div>
                    
                    {/* Row 3: Description (span 3) */}
                    <div>
                      <Label className={colors.textSecondary}>Description</Label>
                      <Input
                        value={editedPrompt.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                        placeholder="Brief description of what this AI agent does..."
                      />
                    </div>
                    
                    {/* Row 4: User Personality Injection (span 3) */}
                    <Card className={cn(
                      "p-4 border-2",
                      editedPrompt.context_config?.include_personality !== false
                        ? "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700"
                        : cn(colors.bgCard, colors.borderPrimary)
                    )}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            editedPrompt.context_config?.include_personality !== false
                              ? "bg-purple-100 dark:bg-purple-900/40"
                              : colors.bgTertiary
                          )}>
                            <Users className={cn(
                              "w-5 h-5",
                              editedPrompt.context_config?.include_personality !== false
                                ? "text-purple-600 dark:text-purple-400"
                                : colors.textMuted
                            )} />
                          </div>
                          <div>
                            <h3 className={cn("font-semibold", colors.textPrimary)}>
                              User Personality Injection
                            </h3>
                            <p className={cn("text-sm mt-1", colors.textMuted)}>
                              When enabled, the user&apos;s selected AI personality settings (tone, style, preferences) 
                              will be injected into the prompt via <code className={cn("px-1 rounded text-xs", colors.bgTertiary)}>{"{{personality_prompt_section}}"}</code>
                            </p>
                            {editedPrompt.context_config?.include_personality !== false && (
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                                ✓ Personality will be included when user has configured their preferences
                              </p>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={editedPrompt.context_config?.include_personality !== false}
                          onCheckedChange={(v) => updateNestedField('context_config', 'include_personality', v)}
                        />
                      </div>
                    </Card>
                    
                    {/* Row 5: Domains (span 3) - Editable with tag-like UX */}
                    <div>
                      <Label className={colors.textSecondary}>Domains</Label>
                      <div className="mt-1 flex flex-wrap gap-1 items-center">
                        {editedPrompt.domains?.map(domain => (
                          <span 
                            key={domain} 
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full capitalize",
                              "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                            )}
                          >
                            {domain}
                            {editedPrompt.domains?.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newDomains = editedPrompt.domains.filter(d => d !== domain);
                                  updateField('domains', newDomains);
                                }}
                                className="ml-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-dashed transition-colors",
                                colors.borderPrimary, colors.textMuted,
                                "hover:border-blue-400 hover:text-blue-500"
                              )}
                            >
                              <Plus className="w-3 h-3" />
                              Add more...
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className={cn("w-56 p-2", colors.bgCard, colors.borderPrimary)} align="start">
                            <div className="space-y-1">
                              <p className={cn("text-xs font-medium mb-2", colors.textMuted)}>Select domain to add</p>
                              {domains
                                .filter(d => !editedPrompt.domains?.includes(d.value))
                                .map(domain => (
                                  <button
                                    key={domain.value}
                                    onClick={() => {
                                      const newDomains = [...(editedPrompt.domains || []), domain.value];
                                      updateField('domains', newDomains);
                                    }}
                                    className={cn(
                                      "w-full text-left px-2 py-1.5 text-sm rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors",
                                      colors.textPrimary
                                    )}
                                  >
                                    <span className="font-medium">{domain.label}</span>
                                    <p className={cn("text-xs", colors.textMuted)}>{domain.description}</p>
                                  </button>
                                ))}
                              {domains.filter(d => !editedPrompt.domains?.includes(d.value)).length === 0 && (
                                <p className={cn("text-xs text-center py-2", colors.textMuted)}>All domains added</p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                        This prompt appears when working with these domain types. At least one domain is required.
                      </p>
                    </div>
                  </TabsContent>
                  
                  {/* ========================================================= */}
                  {/* WORKFLOW-SPECIFIC TABS                                    */}
                  {/* ========================================================= */}
                  
                  {/* Static Actions Tab - Workflow Only (Read-Only) */}
                  {editedPrompt.category === 'workflow' && (
                    <TabsContent value="workflow-actions" className="p-6 space-y-6 mt-0">
                      <div className={cn("p-3 rounded-lg flex items-start gap-3", "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700")}>
                        <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                        <div>
                          <h4 className={cn("font-medium", colors.textPrimary)}>Static Actions</h4>
                          <p className={cn("text-sm", colors.textMuted)}>
                            Workflow actions are predefined and not AI-triggered. This is a reference list of available actions for this workflow type.
                          </p>
                        </div>
                      </div>
                      
                      {/* Static Actions List */}
                      <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                        <h4 className={cn("font-medium mb-4 flex items-center gap-2", colors.textPrimary)}>
                          <Zap className="w-4 h-4 text-amber-500" />
                          Available Actions
                          <Badge variant="outline" className="ml-2 text-xs">Read-Only</Badge>
                        </h4>
                        
                        {/* Visualization Co-Pilot Actions */}
                        {editedPrompt.agent_type === 'visualization_copilot' && (
                          <div className="space-y-3">
                            {/* Generate Timeline */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-blue-100 dark:bg-blue-900/40">
                                <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Generate Timeline</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>generate_timeline</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Creates an initial roadmap visualization from selected product data
                                </p>
                              </div>
                              <Badge className="bg-blue-500">Primary</Badge>
                            </div>
                            
                            {/* Refine Timeline */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-green-100 dark:bg-green-900/40">
                                <Edit2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Refine Timeline</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>refine_timeline</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Improves existing timeline based on user feedback and prompts
                                </p>
                              </div>
                            </div>
                            
                            {/* Add Items */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-purple-100 dark:bg-purple-900/40">
                                <Plus className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Add Items</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>add_items</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Adds new items to the timeline visualization
                                </p>
                              </div>
                            </div>
                            
                            {/* Adjust Detail Level */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-amber-100 dark:bg-amber-900/40">
                                <Sliders className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Adjust Detail Level</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>change_granularity</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Makes the timeline more or less detailed based on audience needs
                                </p>
                              </div>
                            </div>
                            
                            {/* Reorder Items */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-cyan-100 dark:bg-cyan-900/40">
                                <GripVertical className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Reorder Items</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>reorder_items</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Reorganizes timeline items based on different criteria (date, priority, etc.)
                                </p>
                              </div>
                            </div>
                            
                            <div className={cn("mt-4 p-3 rounded-lg flex items-start gap-2", colors.bgTertiary)}>
                              <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                              <p className={cn("text-xs", colors.textMuted)}>
                                These actions are triggered by user buttons in the visualization editor, not by AI decision. 
                                The AI generates content based on the selected action type.
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Product Wizard Co-Pilot Actions */}
                        {editedPrompt.agent_type === 'product_wizard_copilot' && (
                          <div className="space-y-3">
                            {/* SET_CANVAS */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-blue-100 dark:bg-blue-900/40">
                                <Layout className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Set Canvas</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>SET_CANVAS</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Creates the full product canvas structure with product info, personas, goals, and epics
                                </p>
                              </div>
                              <Badge className="bg-blue-500">Primary</Badge>
                            </div>
                            
                            {/* UPDATE_PRODUCT */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-green-100 dark:bg-green-900/40">
                                <Edit2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Update Product</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>UPDATE_PRODUCT</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Updates product name, description, vision, target audience, or value proposition
                                </p>
                              </div>
                            </div>
                            
                            {/* Persona Actions */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-purple-100 dark:bg-purple-900/40">
                                <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Persona Actions</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>ADD_PERSONA</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>UPDATE_PERSONA</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>REMOVE_PERSONA</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Manage user personas with title, role, goals, pain points, and segment
                                </p>
                              </div>
                            </div>
                            
                            {/* Goal Actions */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-amber-100 dark:bg-amber-900/40">
                                <Target className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Goal Actions</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>ADD_GOAL</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>UPDATE_GOAL</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>REMOVE_GOAL</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Manage product goals with title, description, and success metrics
                                </p>
                              </div>
                            </div>
                            
                            {/* Epic Actions */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-indigo-100 dark:bg-indigo-900/40">
                                <Layers className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Epic Actions</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>ADD_EPIC</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>UPDATE_EPIC</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>REMOVE_EPIC</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Manage epics (feature groups) with name, description, and child features
                                </p>
                              </div>
                            </div>
                            
                            {/* Feature Actions */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-cyan-100 dark:bg-cyan-900/40">
                                <Box className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Feature Actions</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>ADD_FEATURE</code>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>BULK_ADD_FEATURES</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Add features to epics with name, description, and priority (must_have, should_have, nice_to_have)
                                </p>
                              </div>
                            </div>
                            
                            {/* ASK_CLARIFICATION */}
                            <div className={cn("p-3 rounded-lg border flex items-start gap-3", colors.borderPrimary, colors.bgSecondary)}>
                              <div className="p-2 rounded bg-rose-100 dark:bg-rose-900/40">
                                <HelpCircle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Ask Clarification</h5>
                                  <code className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>ASK_CLARIFICATION</code>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  Request more information from the user before proceeding with canvas changes
                                </p>
                              </div>
                            </div>
                            
                            <div className={cn("mt-4 p-3 rounded-lg flex items-start gap-2", colors.bgTertiary)}>
                              <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                              <p className={cn("text-xs", colors.textMuted)}>
                                The AI uses SET_CANVAS on the first turn to build the full product structure, 
                                then uses specific actions (ADD_*, UPDATE_*, REMOVE_*) for incremental changes.
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Other Workflow Co-Pilots - No Special Actions */}
                        {editedPrompt.agent_type !== 'visualization_copilot' && editedPrompt.agent_type !== 'product_wizard_copilot' && (
                          <div className={cn("p-4 rounded-lg text-center", colors.bgSecondary)}>
                            <Info className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className={cn("text-sm", colors.textMuted)}>
                              This workflow co-pilot uses standard conversational actions (AWAIT_FEEDBACK).
                            </p>
                            <p className={cn("text-xs mt-1", colors.textMuted)}>
                              No special canvas or timeline actions are configured.
                            </p>
                          </div>
                        )}
                      </Card>
                    </TabsContent>
                  )}
                  
                  {/* Context Sources Tab - Workflow Only (Read-Only Visual) */}
                  {editedPrompt.category === 'workflow' && (
                    <TabsContent value="workflow-context" className="p-6 space-y-6 mt-0">
                      <div className={cn("p-3 rounded-lg flex items-start gap-3", "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700")}>
                        <Database className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                        <div>
                          <h4 className={cn("font-medium", colors.textPrimary)}>Context Sources</h4>
                          <p className={cn("text-sm", colors.textMuted)}>
                            Overview of what data is loaded into the AI context for this workflow. Use this to audit what the AI can see.
                          </p>
                        </div>
                      </div>
                      
                      {/* Context Sources Overview */}
                      <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                        <h4 className={cn("font-medium mb-4 flex items-center gap-2", colors.textPrimary)}>
                          <Database className="w-4 h-4 text-blue-500" />
                          Release-Centric Context Structure
                          <Badge variant="outline" className="ml-2 text-xs">Full Depth</Badge>
                        </h4>
                        
                        <p className={cn("text-sm mb-4", colors.textMuted)}>
                          Context is structured around Releases for roadmap visualization. Each feature includes its full hierarchy chain.
                        </p>
                        
                        <div className="space-y-4">
                          {/* Products */}
                          <div className={cn("p-3 rounded-lg border", "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20")}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-green-100 dark:bg-green-900/40">
                                  <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Products</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>Selected products for visualization</p>
                                </div>
                              </div>
                              <Badge className="bg-green-500">Full</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              name, description, vision, strategic pillars, value proposition, positioning
                            </p>
                          </div>
                          
                          {/* Personas */}
                          <div className={cn("p-3 rounded-lg border", "border-pink-200 dark:border-pink-800 bg-pink-50 dark:bg-pink-900/20")}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-pink-100 dark:bg-pink-900/40">
                                  <Users className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Personas</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>All personas for audience shaping</p>
                                </div>
                              </div>
                              <Badge className="bg-pink-500">Full</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              name, role, goals, pain points, behaviors
                            </p>
                          </div>
                          
                          {/* Goals with KPIs */}
                          <div className={cn("p-3 rounded-lg border", "border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20")}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-yellow-100 dark:bg-yellow-900/40">
                                  <Target className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Goals with KPIs</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>Business goals with KPI current values</p>
                                </div>
                              </div>
                              <Badge className="bg-yellow-500">Full + KPIs</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              title, description, status, category, KPIs (name, target, current value, unit)
                            </p>
                          </div>
                          
                          {/* Releases */}
                          <div className={cn("p-3 rounded-lg border", "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20")}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-amber-100 dark:bg-amber-900/40">
                                  <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Releases (Primary Structure)</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>Release schedule with nested features</p>
                                </div>
                              </div>
                              <Badge className="bg-amber-500">Full + Nested</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              name, target_date, status, features[] with full hierarchy chain
                            </p>
                          </div>
                          
                          {/* Features (Nested) */}
                          <div className={cn("p-3 rounded-lg border ml-6", "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20")}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-blue-100 dark:bg-blue-900/40">
                                  <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>→ Features</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>Nested under each release</p>
                                </div>
                              </div>
                              <Badge className="bg-blue-500">Full + Requirements</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              title, description, status, priority, ALL requirements[], tags, persona_ids
                            </p>
                          </div>
                          
                          {/* Epic → Initiative → Goal chain */}
                          <div className={cn("p-3 rounded-lg border ml-12", "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20")}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-purple-100 dark:bg-purple-900/40">
                                  <GitBranch className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>→ Epic → Initiative → Goals</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>Full upward hierarchy for each feature</p>
                                </div>
                              </div>
                              <Badge className="bg-purple-500">Full Chain</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              Each feature includes its parent epic, grandparent initiative, and linked goals with KPIs
                            </p>
                          </div>
                          
                          {/* Unscheduled */}
                          <div className={cn("p-3 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-gray-100 dark:bg-gray-800">
                                  <Clock className="w-4 h-4 text-gray-500" />
                                </div>
                                <div>
                                  <h5 className={cn("font-medium", colors.textPrimary)}>Unscheduled Work</h5>
                                  <p className={cn("text-xs", colors.textMuted)}>Features not assigned to any release</p>
                                </div>
                              </div>
                              <Badge variant="outline">Separate Section</Badge>
                            </div>
                            <p className={cn("text-xs mt-2 pl-11", colors.textMuted)}>
                              Same structure as scheduled features, grouped separately
                            </p>
                          </div>
                        </div>
                        
                        <div className={cn("mt-4 p-3 rounded-lg flex items-start gap-2", colors.bgTertiary)}>
                          <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className={cn("text-xs", colors.textMuted)}>
                            <p className="font-medium mb-1">Structure Benefits:</p>
                            <ul className="list-disc list-inside space-y-1">
                              <li><strong>Release-Centric:</strong> Natural for roadmap timelines</li>
                              <li><strong>Full Hierarchy:</strong> Feature → Epic → Initiative → Goal chain</li>
                              <li><strong>KPIs Included:</strong> Current values for board-level context</li>
                              <li><strong>All Requirements:</strong> Complete feature details for team context</li>
                            </ul>
                          </div>
                        </div>
                      </Card>
                    </TabsContent>
                  )}
                  
                  {/* Instructions Tab - NEW */}
                  <TabsContent value="instructions" className="p-6 space-y-6 mt-0">
                    <div className={cn("p-3 rounded-lg flex items-start gap-3", colors.bgSecondary)}>
                      <Code className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <h4 className={cn("font-medium", colors.textPrimary)}>Prompt Instructions</h4>
                        <p className={cn("text-sm", colors.textMuted)}>
                          Define what the AI knows and does. Context, response format, and actions are auto-injected by code.
                        </p>
                      </div>
                    </div>
                    
                    {/* Role Instructions - stored in model_2_config */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <Label className={cn("font-medium", colors.textPrimary)}>Role Instructions</Label>
                        </div>
                        <span className={cn("text-xs", colors.textMuted)}>
                          {editedPrompt.model_2_config?.role_instructions?.length || 0} characters
                        </span>
                      </div>
                      <p className={cn("text-xs mb-2", colors.textMuted)}>
                        Who is this AI? Define its identity, expertise, and purpose.
                      </p>
                      <Textarea
                        value={editedPrompt.model_2_config?.role_instructions || ''}
                        onChange={(e) => updateNestedField('model_2_config', 'role_instructions', e.target.value)}
                        className={cn("font-mono text-sm min-h-[120px]", colors.bgInput, colors.borderPrimary)}
                        placeholder="You are an expert AI Co-Pilot for..."
                      />
                    </div>
                    
                    {/* Task Instructions - Conditional based on agent_type */}
                    {editedPrompt.agent_type === 'visualization_copilot' ? (
                      /* Visualization Co-Pilot: Audience-Specific Task Instructions */
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <Label className={cn("font-medium", colors.textPrimary)}>Audience-Specific Task Instructions</Label>
                          </div>
                        </div>
                        <p className={cn("text-xs mb-2", colors.textMuted)}>
                          Define task instructions for each audience type. Role instructions and guidelines apply globally.
                        </p>
                        
                        {/* Board/Executive */}
                        <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
                              <Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="flex-1">
                              <h4 className={cn("font-semibold", colors.textPrimary)}>Board / Executive</h4>
                              <p className={cn("text-xs", colors.textMuted)}>Strategic overview for leadership and stakeholders</p>
                            </div>
                            <span className={cn("text-xs", colors.textMuted)}>
                              {editedPrompt.audience_configs?.board?.task_instructions?.length || 0} chars
                            </span>
                          </div>
                          <Textarea
                            value={editedPrompt.audience_configs?.board?.task_instructions || ''}
                            onChange={(e) => {
                              const newConfigs = {
                                ...(editedPrompt.audience_configs || {}),
                                board: {
                                  ...(editedPrompt.audience_configs?.board || {}),
                                  task_instructions: e.target.value
                                }
                              };
                              updateField('audience_configs', newConfigs);
                            }}
                            placeholder="Focus on strategic initiatives and business outcomes. Use executive language. Emphasize ROI, market positioning, and high-level milestones..."
                            className={cn("font-mono text-sm min-h-[100px]", colors.bgInput, colors.borderPrimary)}
                          />
                        </Card>
                        
                        {/* Team */}
                        <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <h4 className={cn("font-semibold", colors.textPrimary)}>Team</h4>
                              <p className={cn("text-xs", colors.textMuted)}>Detailed view for internal product and engineering teams</p>
                            </div>
                            <span className={cn("text-xs", colors.textMuted)}>
                              {editedPrompt.audience_configs?.team?.task_instructions?.length || 0} chars
                            </span>
                          </div>
                          <Textarea
                            value={editedPrompt.audience_configs?.team?.task_instructions || ''}
                            onChange={(e) => {
                              const newConfigs = {
                                ...(editedPrompt.audience_configs || {}),
                                team: {
                                  ...(editedPrompt.audience_configs?.team || {}),
                                  task_instructions: e.target.value
                                }
                              };
                              updateField('audience_configs', newConfigs);
                            }}
                            placeholder="Include epics and milestones. Use product management terminology. Show dependencies and technical considerations..."
                            className={cn("font-mono text-sm min-h-[100px]", colors.bgInput, colors.borderPrimary)}
                          />
                        </Card>
                        
                        {/* Customer */}
                        <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/40">
                              <Target className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div className="flex-1">
                              <h4 className={cn("font-semibold", colors.textPrimary)}>Customer</h4>
                              <p className={cn("text-xs", colors.textMuted)}>Public-facing view for customers and prospects</p>
                            </div>
                            <span className={cn("text-xs", colors.textMuted)}>
                              {editedPrompt.audience_configs?.customer?.task_instructions?.length || 0} chars
                            </span>
                          </div>
                          <Textarea
                            value={editedPrompt.audience_configs?.customer?.task_instructions || ''}
                            onChange={(e) => {
                              const newConfigs = {
                                ...(editedPrompt.audience_configs || {}),
                                customer: {
                                  ...(editedPrompt.audience_configs?.customer || {}),
                                  task_instructions: e.target.value
                                }
                              };
                              updateField('audience_configs', newConfigs);
                            }}
                            placeholder="Highlight features and release dates. Keep jargon-free. Focus on user benefits and value delivery..."
                            className={cn("font-mono text-sm min-h-[100px]", colors.bgInput, colors.borderPrimary)}
                          />
                        </Card>
                        
                        {/* Custom */}
                        <Card className={cn("p-4 border-dashed", colors.bgCard, colors.borderPrimary)}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                              <Edit2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </div>
                            <div className="flex-1">
                              <h4 className={cn("font-semibold", colors.textPrimary)}>Custom</h4>
                              <p className={cn("text-xs", colors.textMuted)}>User-defined audience with flexible instructions</p>
                            </div>
                            <span className={cn("text-xs", colors.textMuted)}>
                              {editedPrompt.audience_configs?.custom?.task_instructions?.length || 0} chars
                            </span>
                          </div>
                          <Textarea
                            value={editedPrompt.audience_configs?.custom?.task_instructions || ''}
                            onChange={(e) => {
                              const newConfigs = {
                                ...(editedPrompt.audience_configs || {}),
                                custom: {
                                  ...(editedPrompt.audience_configs?.custom || {}),
                                  task_instructions: e.target.value
                                }
                              };
                              updateField('audience_configs', newConfigs);
                            }}
                            placeholder="Define custom instructions for user-specified audiences..."
                            className={cn("font-mono text-sm min-h-[100px]", colors.bgInput, colors.borderPrimary)}
                          />
                        </Card>
                      </div>
                    ) : (
                      /* All other co-pilots: Single Task Instructions textarea */
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <Label className={cn("font-medium", colors.textPrimary)}>Task Instructions</Label>
                          </div>
                          <span className={cn("text-xs", colors.textMuted)}>
                            {editedPrompt.model_2_config?.task_instructions?.length || 0} characters
                          </span>
                        </div>
                        <p className={cn("text-xs mb-2", colors.textMuted)}>
                          What should this AI do? Define its responsibilities and objectives.
                        </p>
                        <Textarea
                          value={editedPrompt.model_2_config?.task_instructions || ''}
                          onChange={(e) => updateNestedField('model_2_config', 'task_instructions', e.target.value)}
                          className={cn("font-mono text-sm min-h-[200px]", colors.bgInput, colors.borderPrimary)}
                          placeholder="Your core responsibilities:&#10;- Help refine features...&#10;- Suggest improvements..."
                        />
                      </div>
                    )}
                    
                    {/* Guidelines - stored in model_2_config */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                          <Label className={cn("font-medium", colors.textPrimary)}>Guidelines</Label>
                          <Badge variant="outline" className="text-[10px]">Optional</Badge>
                        </div>
                        <span className={cn("text-xs", colors.textMuted)}>
                          {editedPrompt.model_2_config?.guidelines?.length || 0} characters
                        </span>
                      </div>
                      <p className={cn("text-xs mb-2", colors.textMuted)}>
                        How should the AI behave? Define tone, constraints, and best practices.
                      </p>
                      <Textarea
                        value={editedPrompt.model_2_config?.guidelines || ''}
                        onChange={(e) => updateNestedField('model_2_config', 'guidelines', e.target.value)}
                        className={cn("font-mono text-sm min-h-[150px]", colors.bgInput, colors.borderPrimary)}
                        placeholder="Be concise and actionable...&#10;Focus on specific recommendations..."
                      />
                    </div>
                    
                    {/* User Prompt Template (for workflows) */}
                    {editedPrompt.category === 'workflow' && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                            <Label className={cn("font-medium", colors.textPrimary)}>User Prompt Template</Label>
                            <Badge variant="outline" className="text-[10px]">Workflow Only</Badge>
                          </div>
                          <span className={cn("text-xs", colors.textMuted)}>
                            {editedPrompt.user_prompt_template?.length || 0} characters
                          </span>
                        </div>
                        <p className={cn("text-xs mb-2", colors.textMuted)}>
                          Template for structuring the user&apos;s input. Use placeholders like {"{{epic_title}}"}, {"{{feature_list}}"}, etc.
                        </p>
                        <Textarea
                          value={editedPrompt.user_prompt_template || ''}
                          onChange={(e) => updateField('user_prompt_template', e.target.value)}
                          className={cn("font-mono text-sm min-h-[150px]", colors.bgInput, colors.borderPrimary)}
                          placeholder="EPIC TO ANALYZE:&#10;Title: {{epic_title}}&#10;..."
                        />
                      </div>
                    )}
                    
                    {/* Info about auto-injected sections */}
                    <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                      <h4 className={cn("font-medium mb-3 flex items-center gap-2", colors.textPrimary)}>
                        <Settings className="w-4 h-4" />
                        Auto-Injected by Code
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                          <span className={colors.textMuted}>Context</span>
                          <span className={colors.textSecondary}>→ Context tab</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          <span className={colors.textMuted}>Response Format</span>
                          <span className={colors.textSecondary}>→ Response tab</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          <span className={colors.textMuted}>Actions</span>
                          <span className={colors.textSecondary}>→ Actions tab</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                          <span className={colors.textMuted}>Personality</span>
                          <span className={colors.textSecondary}>→ Context tab toggle</span>
                        </div>
                      </div>
                    </Card>
                  </TabsContent>
                  
                  {/* Response Contract Tab */}
                  <TabsContent value="response" className="p-6 space-y-6 mt-0">
                    {(() => {
                      const responseFormat = editedPrompt.response_format || 'markdown';
                      const contract = RESPONSE_CONTRACTS[responseFormat] || RESPONSE_CONTRACTS.markdown;
                      const isCodeLocked = !contract.editable;
                      
                      return (
                        <>
                          {/* Response Format Header */}
                          <Card className={cn(
                            "p-4",
                            isCodeLocked 
                              ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800" 
                              : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                          )}>
                            <div className="flex items-start gap-3">
                              <div className={cn(
                                "p-2 rounded-lg",
                                isCodeLocked ? "bg-amber-100 dark:bg-amber-900/40" : "bg-green-100 dark:bg-green-900/40"
                              )}>
                                {isCodeLocked ? (
                                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                ) : (
                                  <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className={cn("font-semibold", colors.textPrimary)}>
                                    Response Format: {contract.label}
                                  </h3>
                                  <Badge className={isCodeLocked ? "bg-amber-500" : "bg-green-500"}>
                                    {isCodeLocked ? "Code-Locked" : "Flexible"}
                                  </Badge>
                                </div>
                                <p className={cn("text-sm mt-1", colors.textMuted)}>
                                  {contract.description}
                                </p>
                                {contract.note && (
                                  <p className={cn(
                                    "text-sm mt-2 font-medium",
                                    isCodeLocked ? "text-amber-700 dark:text-amber-400" : "text-green-700 dark:text-green-400"
                                  )}>
                                    {contract.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          </Card>
                          
                          {/* JSON Schema - for json and json_with_actions formats */}
                          {(responseFormat === 'json' || responseFormat === 'json_with_actions') && editedPrompt.json_schema && (
                            <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                              <div className="flex items-center gap-2 mb-3">
                                <Code className="w-4 h-4 text-blue-500" />
                                <h4 className={cn("font-semibold", colors.textPrimary)}>
                                  JSON Schema
                                </h4>
                                <Badge className="bg-blue-500 text-white text-xs">Required Structure</Badge>
                              </div>
                              <div className={cn(
                                "p-4 rounded-lg font-mono text-xs overflow-auto max-h-80",
                                "bg-gray-100 dark:bg-gray-900 border",
                                colors.borderPrimary
                              )}>
                                <pre className={colors.textSecondary}>
                                  {JSON.stringify(editedPrompt.json_schema, null, 2)}
                                </pre>
                              </div>
                              <p className={cn("text-xs mt-2 flex items-center gap-1", colors.textMuted)}>
                                <Info className="w-3 h-3" />
                                AI responses must conform to this schema. Use Preview to validate.
                              </p>
                            </Card>
                          )}
                          
                          {/* Example Response - for json_with_actions */}
                          {responseFormat === 'json_with_actions' && contract.exampleResponse && (
                            <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                              <div className="flex items-center gap-2 mb-3">
                                <FileText className="w-4 h-4 text-green-500" />
                                <h4 className={cn("font-semibold", colors.textPrimary)}>
                                  Example Response
                                </h4>
                              </div>
                              <div className={cn(
                                "p-4 rounded-lg font-mono text-xs overflow-auto",
                                "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                              )}>
                                <pre className="text-green-800 dark:text-green-200">
                                  {contract.exampleResponse}
                                </pre>
                              </div>
                              <p className={cn("text-xs mt-2", colors.textMuted)}>
                                <strong>message:</strong> Markdown for display | <strong>actions:</strong> Structured updates with IDs
                              </p>
                            </Card>
                          )}
                          
                          {/* Legacy JSON with Actions - show action block format (OLD FORMAT) */}
                          {responseFormat === 'json_with_actions' && contract.actionBlock && (
                            <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                              <div className="flex items-center gap-2 mb-3">
                                <Code className="w-4 h-4 text-blue-500" />
                                <h4 className={cn("font-semibold", colors.textPrimary)}>
                                  Action Block Format (Read-Only)
                                </h4>
                              </div>
                              <div className={cn(
                                "p-4 rounded-lg font-mono text-sm",
                                "bg-gray-100 dark:bg-gray-900 border",
                                colors.borderPrimary
                              )}>
                                <pre className={colors.textSecondary}>
                                  {contract.actionBlock.format.replace(/\\n/g, '\n')}
                                </pre>
                              </div>
                              <div className="mt-3">
                                <p className={cn("text-xs font-medium mb-2", colors.textSecondary)}>
                                  Available Action Types:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {contract.actionBlock.actionTypes.map(type => (
                                    <Badge key={type} variant="outline" className="text-xs font-mono">
                                      {type}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </Card>
                          )}
                          
                          {/* Expected Schema Documentation for JSON formats */}
                          {(responseFormat === 'json' || responseFormat === 'json_with_actions') && contract.schemas && typeof contract.schemas === 'object' && !Array.isArray(contract.schemas) && (
                            <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                              <div className="flex items-center gap-2 mb-3">
                                <BookOpen className="w-4 h-4 text-purple-500" />
                                <h4 className={cn("font-semibold", colors.textPrimary)}>
                                  Response Structure Documentation
                                </h4>
                              </div>
                              <div className="space-y-4">
                                {Object.entries(contract.schemas).map(([key, schemaInfo]) => (
                                  <div key={key} className={cn("p-3 rounded-lg", colors.bgSecondary)}>
                                    <h5 className={cn("font-medium text-sm", colors.textPrimary)}>
                                      {schemaInfo.name}
                                    </h5>
                                    <p className={cn("text-xs mt-1", colors.textMuted)}>
                                      {schemaInfo.description}
                                    </p>
                                    <div className="mt-2 space-y-1">
                                      {schemaInfo.schema && Object.entries(schemaInfo.schema).map(([field, desc]) => (
                                        <div key={field} className="flex gap-2 text-xs">
                                          <code className={cn(
                                            "px-1.5 py-0.5 rounded",
                                            "bg-gray-200 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
                                          )}>
                                            {field}
                                          </code>
                                          <span className={colors.textMuted}>{desc}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}
                          
                          {/* Info for flexible formats */}
                          {!isCodeLocked && (
                            <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                              <div className="flex items-start gap-3">
                                <Info className={cn("w-5 h-5 mt-0.5", colors.textMuted)} />
                                <div>
                                  <h4 className={cn("font-medium", colors.textPrimary)}>
                                    Flexible Response Format
                                  </h4>
                                  <p className={cn("text-sm mt-1", colors.textMuted)}>
                                    This prompt uses {contract.label.toLowerCase()} format, which means the AI&apos;s response 
                                    is displayed directly to the user without programmatic parsing. You can safely modify 
                                    the system prompt to adjust tone, structure, and content without breaking functionality.
                                  </p>
                                </div>
                              </div>
                            </Card>
                          )}
                        </>
                      );
                    })()}
                  </TabsContent>
                  
                  {/* Context Tab */}
                  <TabsContent value="context" className="p-6 space-y-6 mt-0">
                    {/* Model Architecture Toggle - Single vs Two-Model */}
                    <Card className={cn(
                      "p-4 border-2",
                      editedPrompt.copilot_settings?.bypass_model_1 !== false
                        ? cn(colors.bgCard, colors.borderPrimary)
                        : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700"
                    )}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            editedPrompt.copilot_settings?.bypass_model_1 !== false
                              ? colors.bgTertiary
                              : "bg-amber-100 dark:bg-amber-900/40"
                          )}>
                            <Cpu className={cn(
                              "w-5 h-5",
                              editedPrompt.copilot_settings?.bypass_model_1 !== false
                                ? colors.textMuted
                                : "text-amber-600 dark:text-amber-400"
                            )} />
                          </div>
                          <div>
                            <h3 className={cn("font-semibold", colors.textPrimary)}>
                              Single Model Mode
                            </h3>
                            <p className={cn("text-sm mt-1", colors.textMuted)}>
                              {editedPrompt.copilot_settings?.bypass_model_1 !== false
                                ? "Context is configured manually using presets and toggles below."
                                : "Uses two models: Model 1 (Orchestrator) dynamically selects relevant data, Model 2 generates response."
                              }
                            </p>
                            {editedPrompt.copilot_settings?.bypass_model_1 === false && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                                ⚡ Two-model mode active - configure Model 1 Orchestrator below
                              </p>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={editedPrompt.copilot_settings?.bypass_model_1 !== false}
                          onCheckedChange={(v) => {
                            updateNestedField('copilot_settings', 'bypass_model_1', v);
                            // Initialize model_1_config if enabling two-model mode - use Gemini 3
                            if (!v && !editedPrompt.model_1_config) {
                              setEditedPrompt(prev => ({
                                ...prev,
                                model_1_config: {
                                  provider: 'gemini',
                                  model: 'gemini-3-flash-preview',
                                  temperature: 0.3,
                                  max_tokens: 2000,
                                  thinking_level: 'low',
                                  use_structured_output: true,
                                  system_prompt: ''
                                }
                              }));
                            }
                          }}
                        />
                      </div>
                    </Card>
                    
                    {/* Two-Model Mode: Model 1 Orchestrator Configuration */}
                    {editedPrompt.copilot_settings?.bypass_model_1 === false && (
                      <Card className={cn("p-4 border-2 border-amber-200 dark:border-amber-700", colors.bgCard)}>
                        <h3 className={cn("font-medium mb-4 flex items-center gap-2", colors.textPrimary)}>
                          <Bot className="w-4 h-4 text-amber-500" />
                          Model 1: Orchestrator Configuration (Gemini 3)
                        </h3>
                        
                        <div className="space-y-4">
                          {/* Model 1 System Instructions */}
                          <div>
                            <Label className={colors.textSecondary}>System Instructions</Label>
                            <p className={cn("text-xs mb-2", colors.textMuted)}>
                              Instructions for the orchestrator model on how to select relevant data from the product catalog
                            </p>
                            <Textarea
                              value={editedPrompt.model_1_config?.system_prompt || ''}
                              onChange={(e) => updateNestedField('model_1_config', 'system_prompt', e.target.value)}
                              placeholder="You are a Data Selector. Your job is to identify what data is needed to answer a question about a product..."
                              className={cn("min-h-[120px] font-mono text-sm", colors.bgInput, colors.borderPrimary)}
                            />
                          </div>
                          
                          {/* Model 1 Settings - Gemini 3 only */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label className={colors.textSecondary}>Gemini 3 Model (Orchestrator)</Label>
                              <Select
                                value={editedPrompt.model_1_config?.model || 'gemini-3-flash-preview'}
                                onValueChange={(v) => {
                                  updateNestedField('model_1_config', 'provider', 'gemini');
                                  updateNestedField('model_1_config', 'model', v);
                                }}
                              >
                                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="gemini-3-flash-preview">Gemini 3 Flash (Recommended for orchestration)</SelectItem>
                                  <SelectItem value="gemini-3-pro-preview">Gemini 3 Pro (More reasoning)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className={colors.textSecondary}>Temperature</Label>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                max="2"
                                value={editedPrompt.model_1_config?.temperature || 0.3}
                                onChange={(e) => updateNestedField('model_1_config', 'temperature', parseFloat(e.target.value))}
                                className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                              />
                            </div>
                            <div>
                              <Label className={colors.textSecondary}>Max Tokens</Label>
                              <Input
                                type="number"
                                value={editedPrompt.model_1_config?.max_tokens || 2000}
                                onChange={(e) => updateNestedField('model_1_config', 'max_tokens', parseInt(e.target.value))}
                                className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                              />
                            </div>
                          </div>
                          
                          {/* Orchestrator Tips */}
                          <div className={cn("p-3 rounded-lg flex items-start gap-2", colors.bgSecondary)}>
                            <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5" />
                            <div className={cn("text-xs", colors.textMuted)}>
                              <strong>Orchestrator Tips:</strong>
                              <ul className="mt-1 space-y-1 list-disc list-inside">
                                <li>Use a fast, cheap model (GPT-4o-mini or Gemini Flash) for data selection</li>
                                <li>Keep temperature low (0.2-0.4) for consistent data selection</li>
                                <li>Model 1 sees a lightweight catalog and decides what full data Model 2 needs</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </Card>
                    )}
                    
                    {/* Single Model Mode: Context Presets and Toggles */}
                    {editedPrompt.copilot_settings?.bypass_model_1 !== false && (
                      <>
                    {/* Context Limits - Always visible at top */}
                    <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                      <h3 className={cn("font-medium mb-4", colors.textPrimary)}>
                        Context Limits
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className={colors.textSecondary}>Max Context Tokens</Label>
                          <Input
                            type="number"
                            value={editedPrompt.context_config?.max_context_tokens || 8000}
                            onChange={(e) => updateNestedField('context_config', 'max_context_tokens', parseInt(e.target.value))}
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                          />
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Maximum tokens for context window
                          </p>
                        </div>
                        <div>
                          <Label className={colors.textSecondary}>Max Conversation History</Label>
                          <Input
                            type="number"
                            value={editedPrompt.context_config?.max_conversation_history || 10}
                            onChange={(e) => updateNestedField('context_config', 'max_conversation_history', parseInt(e.target.value))}
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                          />
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Number of previous messages to include
                          </p>
                        </div>
                      </div>
                    </Card>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className={colors.textSecondary}>Context Depth Preset</Label>
                        {getItemTypeFromPrompt(editedPrompt) && (
                          <Badge variant="outline" className="text-xs">
                            {getItemTypeFromPrompt(editedPrompt)} co-pilot
                          </Badge>
                        )}
                      </div>
                      <p className={cn("text-xs mb-2", colors.textMuted)}>
                        Control how much context is loaded for the AI
                      </p>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {Object.entries(DEPTH_PRESETS).map(([key, { label, description, depth_config }]) => (
                          <button
                            key={key}
                            onClick={() => {
                              // Update both context_preset and depth_config
                              setEditedPrompt(prev => ({
                                ...prev,
                                context_preset: key,
                                depth_config: depth_config
                              }));
                              setHasChanges(true);
                            }}
                            className={cn(
                              "p-2 rounded-lg border text-center transition-all",
                              editedPrompt.context_preset === key
                                ? "border-blue-500 bg-blue-500/10"
                                : cn(colors.borderPrimary, "hover:bg-gray-100 dark:hover:bg-gray-800")
                            )}
                          >
                            <div className={cn(
                              "font-medium text-sm",
                              editedPrompt.context_preset === key ? "text-blue-600" : colors.textPrimary
                            )}>
                              {label}
                            </div>
                            <div className={cn("text-xs mt-1", colors.textMuted)}>
                              {description.length > 40 ? description.slice(0, 40) + '...' : description}
                            </div>
                          </button>
                        ))}
                      </div>
                      <p className={cn("text-xs mt-2", colors.textMuted)}>
                        {DEPTH_PRESETS[editedPrompt.context_preset || 'standard']?.description || 'Select a preset'}
                      </p>
                      
                      {/* Full Context Warning */}
                      {editedPrompt.context_preset === 'full' && (
                        <div className={cn(
                          "mt-3 p-3 rounded-lg border",
                          "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700"
                        )}>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className={cn("text-sm font-medium", "text-amber-800 dark:text-amber-200")}>
                                Executive-Level Context
                              </p>
                              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                This loads the complete product database into every AI request: all epics, features, goals, 
                                personas, initiatives, releases, strategy documents, market intelligence, and document summaries. 
                                <strong className="block mt-1">⚠️ High token usage per conversation turn.</strong>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Exclude Siblings Toggle - Only for item co-pilots with Full context */}
                      {editedPrompt.context_preset === 'full' && editedPrompt.category === 'item' && (
                        <div className={cn(
                          "mt-3 p-3 rounded-lg border",
                          "bg-gray-50 dark:bg-gray-800/50",
                          colors.borderPrimary
                        )}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ArrowLeftRight className="w-4 h-4 text-gray-500" />
                              <div>
                                <p className={cn("text-sm font-medium", colors.textPrimary)}>
                                  Exclude Sibling Items
                                </p>
                                <p className={cn("text-xs mt-0.5", colors.textMuted)}>
                                  Reduces token usage by not loading other items of the same type alongside the current one
                                </p>
                              </div>
                            </div>
                            <Switch
                              checked={editedPrompt.copilot_settings?.exclude_siblings === true}
                              onCheckedChange={(v) => {
                                updateNestedField('copilot_settings', 'exclude_siblings', v);
                                setHasChanges(true);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Advanced Context Settings - Depth-based Controls */}
                    {editedPrompt.context_preset !== 'full' && (
                    <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={cn("font-medium", colors.textPrimary)}>
                          Context Depth Configuration
                        </h3>
                        {editedPrompt.context_preset && editedPrompt.context_preset !== 'custom' && (
                          <Badge variant="outline" className="text-xs">
                            Using {editedPrompt.context_preset} preset
                          </Badge>
                        )}
                        {editedPrompt.context_preset === 'custom' && (
                          <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                            Custom
                          </Badge>
                        )}
                      </div>
                      <p className={cn("text-xs mb-4", colors.textMuted)}>
                        Control how much context is loaded in each direction. Changing any setting will switch to Custom mode.
                      </p>
                      
                      {/* Depth-based Controls - ALWAYS ENABLED */}
                      <div className="space-y-6">
                        {/* UPWARD - Parent Chain */}
                        <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                          <div className="flex items-center gap-2 mb-3">
                            <ArrowUp className="w-4 h-4 text-blue-500" />
                            <h4 className={cn("font-medium", colors.textPrimary)}>Upward (Parents)</h4>
                          </div>
                          <p className={cn("text-xs mb-3", colors.textMuted)}>
                            How far up the hierarchy to load (Feature → Epic → Initiative → Goal)
                          </p>
                          <Select
                            value={editedPrompt.depth_config?.upward || 'immediate'}
                            onValueChange={(v) => {
                              const newDepthConfig = { ...(editedPrompt.depth_config || {}), upward: v };
                              setEditedPrompt(prev => ({ ...prev, depth_config: newDepthConfig, context_preset: 'custom' }));
                              setHasChanges(true);
                            }}
                          >
                            <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No parents loaded</SelectItem>
                              <SelectItem value="immediate">Immediate - Direct parent only</SelectItem>
                              <SelectItem value="full_chain">Full Chain - Complete hierarchy</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* OUTWARD - Siblings */}
                        <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                          <div className="flex items-center gap-2 mb-3">
                            <ArrowLeftRight className="w-4 h-4 text-green-500" />
                            <h4 className={cn("font-medium", colors.textPrimary)}>Outward (Siblings)</h4>
                          </div>
                          <p className={cn("text-xs mb-3", colors.textMuted)}>
                            How much detail to load for sibling entities (same parent)
                          </p>
                          <Select
                            value={editedPrompt.depth_config?.outward || 'titles'}
                            onValueChange={(v) => {
                              const newDepthConfig = { ...(editedPrompt.depth_config || {}), outward: v };
                              setEditedPrompt(prev => ({ ...prev, depth_config: newDepthConfig, context_preset: 'custom' }));
                              setHasChanges(true);
                            }}
                          >
                            <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No siblings</SelectItem>
                              <SelectItem value="titles">Titles Only - Just names</SelectItem>
                              <SelectItem value="summary">Summary - Title + status + description</SelectItem>
                              <SelectItem value="full">Full - Complete sibling details</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* DOWNWARD - Children */}
                        <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                          <div className="flex items-center gap-2 mb-3">
                            <ArrowDown className="w-4 h-4 text-purple-500" />
                            <h4 className={cn("font-medium", colors.textPrimary)}>Downward (Children)</h4>
                          </div>
                          <p className={cn("text-xs mb-3", colors.textMuted)}>
                            How much child data to load (requirements, sub-features, etc.)
                          </p>
                          <Select
                            value={editedPrompt.depth_config?.downward || 'counts'}
                            onValueChange={(v) => {
                              const newDepthConfig = { ...(editedPrompt.depth_config || {}), downward: v };
                              setEditedPrompt(prev => ({ ...prev, depth_config: newDepthConfig, context_preset: 'custom' }));
                              setHasChanges(true);
                            }}
                          >
                            <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No children</SelectItem>
                              <SelectItem value="counts">Counts Only - Just totals</SelectItem>
                              <SelectItem value="summary">Summary - Title + status</SelectItem>
                              <SelectItem value="full">Full - Complete child details</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* LINKABLE - N:N Relationship Entities (Pool) */}
                        <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                          <div className="flex items-center gap-2 mb-3">
                            <Link2 className="w-4 h-4 text-purple-500" />
                            <h4 className={cn("font-medium", colors.textPrimary)}>Linkable Items</h4>
                          </div>
                          <p className={cn("text-xs mb-3", colors.textMuted)}>
                            Pool of entities that CAN be linked (all personas, all goals in product)
                          </p>
                          <Select
                            value={editedPrompt.depth_config?.linkable || 'none'}
                            onValueChange={(v) => {
                              const newDepthConfig = { ...(editedPrompt.depth_config || {}), linkable: v };
                              setEditedPrompt(prev => ({ ...prev, depth_config: newDepthConfig, context_preset: 'custom' }));
                              setHasChanges(true);
                            }}
                          >
                            <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No linkable pool</SelectItem>
                              <SelectItem value="titles">Titles Only - IDs + names</SelectItem>
                              <SelectItem value="summary">Summary - Title + description</SelectItem>
                              <SelectItem value="full">Full - Complete details</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* LINKED - Actually Connected Entities */}
                        <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                          <div className="flex items-center gap-2 mb-3">
                            <GitBranch className="w-4 h-4 text-cyan-500" />
                            <h4 className={cn("font-medium", colors.textPrimary)}>Linked Items</h4>
                          </div>
                          <p className={cn("text-xs mb-3", colors.textMuted)}>
                            Entities actually linked to this item (assigned personas, referencing features)
                          </p>
                          <Select
                            value={editedPrompt.depth_config?.linked || 'none'}
                            onValueChange={(v) => {
                              const newDepthConfig = { ...(editedPrompt.depth_config || {}), linked: v };
                              setEditedPrompt(prev => ({ ...prev, depth_config: newDepthConfig, context_preset: 'custom' }));
                              setHasChanges(true);
                            }}
                          >
                            <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No linked items</SelectItem>
                              <SelectItem value="titles">Titles Only - IDs + names</SelectItem>
                              <SelectItem value="summary">Summary - Title + description</SelectItem>
                              <SelectItem value="full">Full - Complete details</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* PRODUCT - Strategic Context */}
                        <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgSecondary)}>
                          <div className="flex items-center gap-2 mb-3">
                            <Building2 className="w-4 h-4 text-amber-500" />
                            <h4 className={cn("font-medium", colors.textPrimary)}>Product Context</h4>
                          </div>
                          <p className={cn("text-xs mb-3", colors.textMuted)}>
                            How much product-level context to include (vision, strategy, market)
                          </p>
                          <Select
                            value={editedPrompt.depth_config?.product || 'basic'}
                            onValueChange={(v) => {
                              const newDepthConfig = { ...(editedPrompt.depth_config || {}), product: v };
                              setEditedPrompt(prev => ({ ...prev, depth_config: newDepthConfig, context_preset: 'custom' }));
                              setHasChanges(true);
                            }}
                          >
                            <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No product context</SelectItem>
                              <SelectItem value="basic">Basic - Name + description</SelectItem>
                              <SelectItem value="strategic">Strategic - + Vision, pillars</SelectItem>
                              <SelectItem value="full">Full - + Market, competitors, personas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </Card>
                    )}
                      </>
                    )}
                  </TabsContent>
                  
                  {/* Model Tab - uses model_2_config (unified schema) */}
                  <TabsContent value="model" className="p-6 space-y-6 mt-0">
                    {/* Gemini 3 Migration Notice */}
                    <Card className="p-4 bg-blue-500/10 border-blue-500/30 border">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-600 dark:text-blue-400">Gemini 3 Structured Output Mode</h4>
                          <p className="text-sm text-blue-600/80 dark:text-blue-400/80 mt-1">
                            All co-pilots now use Gemini 3 with API-enforced JSON Schema. This eliminates JSON parsing errors 
                            and ensures reliable structured responses. Model selection below configures the Gemini 3 variant.
                          </p>
                        </div>
                      </div>
                    </Card>
                    
                    <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                      <h3 className={cn("font-medium mb-4", colors.textPrimary)}>
                        Model Configuration (Executor)
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Model Selection - Gemini 3 Only */}
                        <div className="col-span-2">
                          <Label className={colors.textSecondary}>Gemini 3 Model</Label>
                          <Select
                            value={editedPrompt.model_2_config?.model || 'gemini-3-flash-preview'}
                            onValueChange={(v) => {
                              updateNestedField('model_2_config', 'provider', 'gemini');
                              updateNestedField('model_2_config', 'model', v);
                              // Set default thinking_level based on model
                              if (v.includes('pro')) {
                                updateNestedField('model_2_config', 'thinking_level', 'medium');
                              } else {
                                updateNestedField('model_2_config', 'thinking_level', 'low');
                              }
                            }}
                          >
                            <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gemini-3-flash-preview">
                                <div className="flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-yellow-500" />
                                  <span>Gemini 3 Flash</span>
                                  <span className="text-xs text-muted-foreground">(Recommended - Fast, Structured Output)</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="gemini-3-pro-preview">
                                <div className="flex items-center gap-2">
                                  <Brain className="w-4 h-4 text-purple-500" />
                                  <span>Gemini 3 Pro</span>
                                  <span className="text-xs text-muted-foreground">(Most Powerful, Structured Output)</span>
                                </div>
                              </SelectItem>
                              {/* Legacy models for backwards compatibility */}
                              <SelectItem value="gemini-2.5-flash" disabled={false}>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">Gemini 2.5 Flash</span>
                                  <span className="text-xs text-orange-500">(Legacy - No Structured Output)</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Structured Output: {editedPrompt.model_2_config?.model?.includes('gemini-3') ? '✓ Enabled' : '✗ Legacy Mode'}
                          </p>
                        </div>
                        
                        {/* Thinking Level - Gemini 3 specific */}
                        <div>
                          <Label className={colors.textSecondary}>
                            Thinking Level
                            <span className="ml-1 text-xs text-blue-500">(Gemini 3)</span>
                          </Label>
                          <Select
                            value={editedPrompt.model_2_config?.thinking_level || 'low'}
                            onValueChange={(v) => updateNestedField('model_2_config', 'thinking_level', v)}
                          >
                            <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None - No reasoning tokens</SelectItem>
                              <SelectItem value="low">Low - 1K tokens (~$0.001)</SelectItem>
                              <SelectItem value="medium">Medium - 4K tokens (~$0.004)</SelectItem>
                              <SelectItem value="high">High - 16K tokens (~$0.016)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Higher = deeper reasoning, more accurate but slower/costlier
                          </p>
                        </div>
                        
                        {/* Media Resolution - Gemini 3 specific */}
                        <div>
                          <Label className={colors.textSecondary}>
                            Media Resolution
                            <span className="ml-1 text-xs text-blue-500">(Gemini 3)</span>
                          </Label>
                          <Select
                            value={editedPrompt.model_2_config?.media_resolution || 'auto'}
                            onValueChange={(v) => updateNestedField('model_2_config', 'media_resolution', v)}
                          >
                            <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto - Let model decide</SelectItem>
                              <SelectItem value="low">Low - Faster, less detail</SelectItem>
                              <SelectItem value="medium">Medium - Balanced</SelectItem>
                              <SelectItem value="high">High - Maximum detail</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            For image/video attachments in co-pilot chats
                          </p>
                        </div>
                        
                        <div>
                          <Label className={colors.textSecondary}>Temperature ({editedPrompt.model_2_config?.temperature || 0.7})</Label>
                          <Input
                            type="range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={editedPrompt.model_2_config?.temperature || 0.7}
                            onChange={(e) => updateNestedField('model_2_config', 'temperature', parseFloat(e.target.value))}
                            className="mt-1"
                          />
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Lower = more focused, Higher = more creative
                          </p>
                        </div>
                        <div>
                          <Label className={colors.textSecondary}>Max Output Tokens</Label>
                          <Input
                            type="number"
                            value={editedPrompt.model_2_config?.max_tokens || 8000}
                            onChange={(e) => updateNestedField('model_2_config', 'max_tokens', parseInt(e.target.value))}
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                          />
                        </div>
                        <div>
                          <Label className={colors.textSecondary}>Retry Attempts</Label>
                          <Input
                            type="number"
                            value={editedPrompt.model_2_config?.retry_attempts || 3}
                            onChange={(e) => updateNestedField('model_2_config', 'retry_attempts', parseInt(e.target.value))}
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                          />
                        </div>
                        <div>
                          <Label className={colors.textSecondary}>Retry Delay (seconds)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={editedPrompt.model_2_config?.retry_delay || 2}
                            onChange={(e) => updateNestedField('model_2_config', 'retry_delay', parseFloat(e.target.value))}
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                          />
                        </div>
                        
                        {/* Structured Output Info (Always Enabled) */}
                        <div className="col-span-2">
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                            <Shield className="w-4 h-4 text-green-500" />
                            <div>
                              <Label className="text-green-600 dark:text-green-400">Structured Output Enabled</Label>
                              <p className="text-xs text-green-600/70 dark:text-green-400/70">
                                JSON Schema enforcement is always active - eliminates parsing errors
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </TabsContent>
                  
                  {/* Actions Tab */}
                  <TabsContent value="actions" className="p-6 space-y-6 mt-0">
                    {/* Unified Actions Editor - Groups by scope, shows all actions and fields */}
                    <UnifiedActionsEditor
                      editedPrompt={editedPrompt}
                      actionRegistry={actionRegistry}
                      fieldRegistry={fieldRegistry}
                      availableDomains={domains}
                      onChange={(field, value) => updateField(field, value)}
                      colors={colors}
                    />
                    
                    {/* Legacy Actions Display (read-only, for reference) */}
                    {editedPrompt.actions && editedPrompt.actions.length > 0 && (
                      <Collapsible>
                        <CollapsibleTrigger className={cn("flex items-center gap-2 text-sm", colors.textMuted)}>
                          <ChevronDown className="w-4 h-4" />
                          Legacy Actions ({editedPrompt.actions.length}) - View Only
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <Card className={cn("p-4 mt-2 opacity-60", colors.bgCard, colors.borderPrimary)}>
                            <p className={cn("text-xs mb-3", colors.textMuted)}>
                              These are the old action definitions. They will be migrated to the new format above.
                            </p>
                            <div className="space-y-2">
                              {editedPrompt.actions.map((action) => (
                                <div key={action.action_id} className="flex items-center gap-2 text-sm">
                                  <Badge variant={action.is_enabled ? "default" : "outline"} className="text-xs">
                                    {action.is_enabled ? "ON" : "OFF"}
                                  </Badge>
                                  <span className={colors.textPrimary}>{action.name}</span>
                                </div>
                              ))}
                            </div>
                          </Card>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </TabsContent>
                  
                  {/* Co-Pilot Settings Tab - Item Co-Pilots */}
                  <TabsContent value="copilot-settings" className="p-6 space-y-6 mt-0">
                    <div className="space-y-6">
                      {/* Header */}
                      <div>
                        <h3 className={cn("font-bold uppercase tracking-wide", colors.textPrimary)}>
                          {editedPrompt.category === 'workflow' ? 'Workflow Settings' : 'Co-Pilot UI Settings'}
                        </h3>
                        <p className={cn("text-sm mt-1", colors.textMuted)}>
                          {editedPrompt.category === 'workflow' 
                            ? 'Configure quick actions and response behavior for this workflow.' 
                            : 'Configure how this co-pilot appears and behaves in the UI sidebar.'}
                        </p>
                      </div>
                      
                      {/* Display Settings - Only for non-workflow */}
                      {editedPrompt.category !== 'workflow' && (
                        <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                          <h4 className={cn("font-medium mb-4", colors.textPrimary)}>Display Settings</h4>
                          <div className="space-y-4">
                            <div>
                              <Label className={colors.textSecondary}>Display Name</Label>
                              <Input
                                value={editedPrompt.copilot_settings?.display_name || ''}
                                onChange={(e) => {
                                  const settings = { ...(editedPrompt.copilot_settings || {}), display_name: e.target.value };
                                  updateField('copilot_settings', settings);
                                }}
                                placeholder={editedPrompt.name || 'Co-Pilot'}
                                className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                              />
                              <p className={cn("text-xs mt-1", colors.textMuted)}>
                                Name shown in the sidebar header. Defaults to the agent name if empty.
                              </p>
                            </div>
                            
                            <div>
                              <Label className={colors.textSecondary}>Welcome Message</Label>
                              <Textarea
                                value={editedPrompt.copilot_settings?.welcome_message || ''}
                                onChange={(e) => {
                                  const settings = { ...(editedPrompt.copilot_settings || {}), welcome_message: e.target.value };
                                  updateField('copilot_settings', settings);
                                }}
                                placeholder="👋 Hi! I'm here to help with this item. I can help you:&#10;• **Define** the problem statement&#10;• **Suggest** success metrics&#10;• **Refine** the description&#10;&#10;What would you like to work on?"
                                rows={5}
                                className={cn("mt-1 font-mono text-sm", colors.bgInput, colors.borderPrimary)}
                              />
                              <p className={cn("text-xs mt-1", colors.textMuted)}>
                                Initial greeting shown when no chat history exists. Supports markdown. Use {'{itemType}'} as placeholder.
                              </p>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div>
                                <Label className={colors.textSecondary}>Summary on Close</Label>
                                <p className={cn("text-xs", colors.textMuted)}>
                                  Generate a conversation summary when the modal closes
                                </p>
                              </div>
                              <Switch
                                checked={editedPrompt.copilot_settings?.summary_on_close !== false}
                                onCheckedChange={(v) => {
                                  const settings = { ...(editedPrompt.copilot_settings || {}), summary_on_close: v };
                                  updateField('copilot_settings', settings);
                                }}
                              />
                            </div>
                          </div>
                        </Card>
                      )}
                      
                      {/* Response Rendering - For all */}
                      <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                        <h4 className={cn("font-medium mb-4", colors.textPrimary)}>Response Settings</h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className={colors.textSecondary}>Enable Entity Links</Label>
                              <p className={cn("text-xs", colors.textMuted)}>
                                Make entity IDs clickable in responses
                              </p>
                            </div>
                            <Switch
                              checked={editedPrompt.copilot_settings?.enable_entity_links !== false}
                              onCheckedChange={(v) => {
                                const settings = { ...(editedPrompt.copilot_settings || {}), enable_entity_links: v };
                                updateField('copilot_settings', settings);
                              }}
                            />
                          </div>
                        </div>
                      </Card>
                      
                      {/* ================================================================ */}
                      {/* WORKFLOW QUICK ACTIONS - Audience-based groups                  */}
                      {/* ================================================================ */}
                      {editedPrompt.category === 'workflow' && (
                        <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                          <div className="mb-4">
                            <h4 className={cn("font-medium", colors.textPrimary)}>Quick Actions</h4>
                            <p className={cn("text-xs", colors.textMuted)}>
                              Configure buttons shown in the co-pilot. General actions are always shown. Audience-specific actions appear based on selected audience.
                            </p>
                          </div>
                          
                          {/* Helper to render a quick action group */}
                          {(() => {
                            const renderQuickActionGroup = (groupKey, groupLabel, groupIcon, maxActions = 3) => {
                              const GroupIcon = groupIcon;
                              const quickActions = editedPrompt.copilot_settings?.quick_actions || {};
                              const actions = quickActions[groupKey] || [];
                              
                              return (
                                <div className={cn("p-4 rounded-lg border mb-4", colors.borderPrimary, colors.bgSecondary)}>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <GroupIcon className="w-4 h-4 text-purple-500" />
                                      <span className={cn("font-medium text-sm", colors.textPrimary)}>{groupLabel}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {actions.length}/{maxActions}
                                      </Badge>
                                    </div>
                                    {actions.length < maxActions && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          const settings = editedPrompt.copilot_settings || {};
                                          const qa = { ...(settings.quick_actions || {}) };
                                          qa[groupKey] = [...(qa[groupKey] || []), {
                                            id: `action_${Date.now()}`,
                                            label: 'New Action',
                                            icon: 'Sparkles',
                                            prompt: '',
                                            is_enabled: true
                                          }];
                                          updateField('copilot_settings', { ...settings, quick_actions: qa });
                                        }}
                                        className="h-7 text-xs"
                                      >
                                        <Plus className="w-3 h-3 mr-1" />
                                        Add
                                      </Button>
                                    )}
                                  </div>
                                  
                                  {actions.length > 0 ? (
                                    <div className="space-y-2">
                                      {actions.map((action, index) => (
                                        <div 
                                          key={action.id || index}
                                          className={cn(
                                            "p-3 rounded-lg border",
                                            action.is_enabled ? colors.borderPrimary : "border-gray-300 dark:border-gray-600 opacity-60",
                                            colors.bgTertiary
                                          )}
                                        >
                                          <div className="flex items-start gap-3">
                                            <div className="flex-1 space-y-2">
                                              <div className="flex items-center gap-2">
                                                <Input
                                                  value={action.label || ''}
                                                  onChange={(e) => {
                                                    const settings = editedPrompt.copilot_settings || {};
                                                    const qa = { ...(settings.quick_actions || {}) };
                                                    const groupActions = [...(qa[groupKey] || [])];
                                                    groupActions[index] = { ...action, label: e.target.value };
                                                    qa[groupKey] = groupActions;
                                                    updateField('copilot_settings', { ...settings, quick_actions: qa });
                                                  }}
                                                  placeholder="Button Label"
                                                  className={cn("h-8 font-medium flex-1", colors.bgInput, colors.borderPrimary)}
                                                />
                                                <Select
                                                  value={action.icon || 'Sparkles'}
                                                  onValueChange={(v) => {
                                                    const settings = editedPrompt.copilot_settings || {};
                                                    const qa = { ...(settings.quick_actions || {}) };
                                                    const groupActions = [...(qa[groupKey] || [])];
                                                    groupActions[index] = { ...action, icon: v };
                                                    qa[groupKey] = groupActions;
                                                    updateField('copilot_settings', { ...settings, quick_actions: qa });
                                                  }}
                                                >
                                                  <SelectTrigger className={cn("w-32 h-8", colors.bgInput, colors.borderPrimary)}>
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="Sparkles">✨ Sparkles</SelectItem>
                                                    <SelectItem value="Wand2">🪄 Wand</SelectItem>
                                                    <SelectItem value="Target">🎯 Target</SelectItem>
                                                    <SelectItem value="Layers">📚 Layers</SelectItem>
                                                    <SelectItem value="Lightbulb">💡 Lightbulb</SelectItem>
                                                    <SelectItem value="Zap">⚡ Zap</SelectItem>
                                                    <SelectItem value="Users">👥 Users</SelectItem>
                                                    <SelectItem value="GitBranch">🔀 Branch</SelectItem>
                                                    <SelectItem value="RefreshCw">🔄 Refresh</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                              <Textarea
                                                value={action.prompt || ''}
                                                onChange={(e) => {
                                                  const settings = editedPrompt.copilot_settings || {};
                                                  const qa = { ...(settings.quick_actions || {}) };
                                                  const groupActions = [...(qa[groupKey] || [])];
                                                  groupActions[index] = { ...action, prompt: e.target.value };
                                                  qa[groupKey] = groupActions;
                                                  updateField('copilot_settings', { ...settings, quick_actions: qa });
                                                }}
                                                placeholder="Prompt to send when clicked..."
                                                rows={2}
                                                className={cn("text-sm", colors.bgInput, colors.borderPrimary)}
                                              />
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <Switch
                                                checked={action.is_enabled !== false}
                                                onCheckedChange={(v) => {
                                                  const settings = editedPrompt.copilot_settings || {};
                                                  const qa = { ...(settings.quick_actions || {}) };
                                                  const groupActions = [...(qa[groupKey] || [])];
                                                  groupActions[index] = { ...action, is_enabled: v };
                                                  qa[groupKey] = groupActions;
                                                  updateField('copilot_settings', { ...settings, quick_actions: qa });
                                                }}
                                              />
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                  const settings = editedPrompt.copilot_settings || {};
                                                  const qa = { ...(settings.quick_actions || {}) };
                                                  qa[groupKey] = (qa[groupKey] || []).filter((_, i) => i !== index);
                                                  updateField('copilot_settings', { ...settings, quick_actions: qa });
                                                }}
                                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className={cn("text-center py-4 text-xs", colors.textMuted)}>
                                      No actions configured. Click "Add" to create one.
                                    </div>
                                  )}
                                </div>
                              );
                            };
                            
                            return (
                              <>
                                {renderQuickActionGroup('general', 'General (Always Shown)', Zap, 3)}
                                {renderQuickActionGroup('board', 'Board / Executive', Building2, 3)}
                                {renderQuickActionGroup('team', 'Team', Users, 3)}
                                {renderQuickActionGroup('customer', 'Customer', Target, 3)}
                                {renderQuickActionGroup('custom', 'Custom Audience', Edit2, 3)}
                              </>
                            );
                          })()}
                        </Card>
                      )}
                      
                      {/* Standard Quick Actions - Only for non-workflow */}
                      {editedPrompt.category !== 'workflow' && (
                      <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className={cn("font-medium", colors.textPrimary)}>Quick Actions</h4>
                            <p className={cn("text-xs", colors.textMuted)}>
                              Buttons shown in the co-pilot header for one-click actions
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => {
                              const settings = editedPrompt.copilot_settings || {};
                              const quickActions = [...(settings.quick_actions || []), {
                                id: `action_${Date.now()}`,
                                label: 'New Action',
                                icon: 'Sparkles',
                                prompt: '',
                                is_enabled: true
                              }];
                              updateField('copilot_settings', { ...settings, quick_actions: quickActions });
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </div>
                        
                        {(editedPrompt.copilot_settings?.quick_actions || []).length > 0 ? (
                          <div className="space-y-3">
                            {(editedPrompt.copilot_settings?.quick_actions || []).map((action, index) => (
                              <div 
                                key={action.id || index} 
                                className={cn(
                                  "p-3 rounded-lg border",
                                  action.is_enabled ? colors.borderPrimary : "border-gray-300 dark:border-gray-600 opacity-60",
                                  colors.bgTertiary
                                )}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <Input
                                        value={action.label || ''}
                                        onChange={(e) => {
                                          const settings = editedPrompt.copilot_settings || {};
                                          const quickActions = [...(settings.quick_actions || [])];
                                          quickActions[index] = { ...action, label: e.target.value };
                                          updateField('copilot_settings', { ...settings, quick_actions: quickActions });
                                        }}
                                        placeholder="Button Label"
                                        className={cn("h-8 font-medium", colors.bgInput, colors.borderPrimary)}
                                      />
                                      <Select
                                        value={action.icon || 'Sparkles'}
                                        onValueChange={(v) => {
                                          const settings = editedPrompt.copilot_settings || {};
                                          const quickActions = [...(settings.quick_actions || [])];
                                          quickActions[index] = { ...action, icon: v };
                                          updateField('copilot_settings', { ...settings, quick_actions: quickActions });
                                        }}
                                      >
                                        <SelectTrigger className={cn("w-32 h-8", colors.bgInput, colors.borderPrimary)}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="Sparkles">✨ Sparkles</SelectItem>
                                          <SelectItem value="Target">🎯 Target</SelectItem>
                                          <SelectItem value="Layers">📚 Layers</SelectItem>
                                          <SelectItem value="Lightbulb">💡 Lightbulb</SelectItem>
                                          <SelectItem value="Zap">⚡ Zap</SelectItem>
                                          <SelectItem value="Search">🔍 Search</SelectItem>
                                          <SelectItem value="RefreshCw">🔄 Refresh</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <Textarea
                                      value={action.prompt || ''}
                                      onChange={(e) => {
                                        const settings = editedPrompt.copilot_settings || {};
                                        const quickActions = [...(settings.quick_actions || [])];
                                        quickActions[index] = { ...action, prompt: e.target.value };
                                        updateField('copilot_settings', { ...settings, quick_actions: quickActions });
                                      }}
                                      placeholder="Prompt to send when clicked (e.g., 'Give me a quick assessment of this {itemType}')"
                                      rows={2}
                                      className={cn("text-sm", colors.bgInput, colors.borderPrimary)}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={action.is_enabled !== false}
                                      onCheckedChange={(v) => {
                                        const settings = editedPrompt.copilot_settings || {};
                                        const quickActions = [...(settings.quick_actions || [])];
                                        quickActions[index] = { ...action, is_enabled: v };
                                        updateField('copilot_settings', { ...settings, quick_actions: quickActions });
                                      }}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const settings = editedPrompt.copilot_settings || {};
                                        const quickActions = (settings.quick_actions || []).filter((_, i) => i !== index);
                                        updateField('copilot_settings', { ...settings, quick_actions: quickActions });
                                      }}
                                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={cn("text-center py-6 rounded-lg border-2 border-dashed", colors.borderPrimary)}>
                            <Sparkles className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
                            <p className={cn("text-sm", colors.textMuted)}>
                              No quick actions configured. Add buttons for common operations.
                            </p>
                          </div>
                        )}
                      </Card>
                      )}
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </div>
          )}
        </div>
      </div>
      
      {/* Version History Modal */}
      <Dialog open={showVersionModal} onOpenChange={setShowVersionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              View previous versions and rollback if needed.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            {loadingVersions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : versions.length > 0 ? (
              <div className="space-y-2">
                {versions.map((version) => (
                  <div
                    key={version.version}
                    className={cn(
                      "p-3 rounded-lg border flex items-center justify-between",
                      version.is_current ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200" : colors.borderPrimary
                    )}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Version {version.version}</span>
                        {version.is_current && (
                          <Badge className="bg-blue-500 text-white text-xs">Current</Badge>
                        )}
                      </div>
                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                        {new Date(version.updated_at).toLocaleString()}
                      </p>
                      {version.change_note && (
                        <p className={cn("text-xs mt-1", colors.textMuted)}>
                          {version.change_note}
                        </p>
                      )}
                    </div>
                    {!version.is_current && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRollback(version.version)}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Rollback
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className={cn("text-center py-8", colors.textMuted)}>No version history available.</p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* Preview Modal - Enhanced */}
      <Dialog open={showPreviewModal} onOpenChange={(open) => {
        setShowPreviewModal(open);
        if (open) {
          loadPreviewProducts();
          setPreviewResult(null);
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" />
              {editedPrompt?.category === 'collection' 
                ? 'Preview Collection Co-Pilot (Two-Model Flow)'
                : 'Preview Prompt with Real Data'
              }
            </DialogTitle>
            <DialogDescription>
              {editedPrompt?.category === 'collection' 
                ? 'Select a context to preview. You\'ll see what each model receives and responds with.'
                : 'Select an item from your data to test this prompt. You\'ll see the assembled prompt and AI response.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Collection Co-Pilot Preview - Different UI */}
            {editedPrompt?.category === 'collection' ? (
              <>
                {/* Collection Context Selection */}
                <div className={cn("p-4 rounded-lg space-y-3", colors.bgSecondary)}>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Product Selection */}
                    <div>
                      <Label className={cn("text-xs mb-1 block", colors.textMuted)}>Product</Label>
                      <Select 
                        value={previewSelectedProduct} 
                        onValueChange={(v) => {
                          setPreviewSelectedProduct(v);
                          setPreviewSelectedItem('');
                          // For collection co-pilots, load parent items based on collection_type
                          const collectionType = editedPrompt.collection_type || editedPrompt.domains?.[0] || 'features';
                          // For features collection, parent context is epics
                          // For epics collection, parent context is product
                          // etc.
                          if (collectionType === 'features') {
                            loadPreviewItems(v, 'epics');
                          } else {
                            // For other collections, product is the context
                            setPreviewSelectedItem(v);
                          }
                        }}
                      >
                        <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                          <SelectValue placeholder="Select product..." />
                        </SelectTrigger>
                        <SelectContent>
                          {previewProducts.map(p => (
                            <SelectItem key={p.product_id} value={p.product_id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Parent Context Selection (for collections that need it) */}
                    <div>
                      <Label className={cn("text-xs mb-1 block", colors.textMuted)}>
                        {editedPrompt.collection_type === 'features' ? 'Epic (Parent Context)' : 'Context'}
                      </Label>
                      {editedPrompt.collection_type === 'features' ? (
                        <Select 
                          value={previewSelectedItem} 
                          onValueChange={setPreviewSelectedItem}
                          disabled={!previewSelectedProduct || loadingPreviewItems}
                        >
                          <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder={loadingPreviewItems ? "Loading epics..." : "Select epic..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {previewItems.map(item => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className={cn("h-9 flex items-center px-3 rounded-md border", colors.bgInput, colors.borderPrimary)}>
                          <span className={cn("text-sm", colors.textMuted)}>Product-level collection</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Test Message */}
                  <div>
                    <Label className={cn("text-xs mb-1 block", colors.textMuted)}>User Query</Label>
                    <div className="flex gap-2">
                      <Input
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        placeholder="Find gaps in feature coverage..."
                        className={cn("flex-1", colors.bgInput, colors.borderPrimary)}
                      />
                      <Button 
                        onClick={handleFullPreview} 
                        disabled={previewLoading || !previewSelectedProduct}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Run Preview
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Two-Model Flow Preview Info */}
                {!previewResult && (
                  <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                    <h4 className={cn("font-medium mb-3 flex items-center gap-2", colors.textPrimary)}>
                      <Cpu className="w-4 h-4 text-blue-500" />
                      Two-Model Preview Flow
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Model 1</Badge>
                          <span className={cn("font-medium text-sm", colors.textPrimary)}>Orchestrator</span>
                        </div>
                        <ul className={cn("text-xs space-y-1", colors.textMuted)}>
                          <li>• Receives: Catalog metadata + User query</li>
                          <li>• Returns: Data request + Crafted prompt</li>
                          <li>• Model: {editedPrompt.model_1_config?.model || 'gpt-4o-mini'}</li>
                        </ul>
                      </div>
                      <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Model 2</Badge>
                          <span className={cn("font-medium text-sm", colors.textPrimary)}>Executor</span>
                        </div>
                        <ul className={cn("text-xs space-y-1", colors.textMuted)}>
                          <li>• Receives: Crafted prompt + Fetched data</li>
                          <li>• Returns: Analysis + Suggested actions</li>
                          <li>• Model: {editedPrompt.model_2_config?.model || 'gpt-4o'}</li>
                        </ul>
                      </div>
                    </div>
                    
                    {/* Actions Preview */}
                    {editedPrompt.actions?.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h5 className={cn("text-xs font-medium mb-2", colors.textMuted)}>Available Actions ({editedPrompt.actions.filter(a => a.is_enabled !== false).length} enabled)</h5>
                        <div className="flex flex-wrap gap-2">
                          {editedPrompt.actions.filter(a => a.is_enabled !== false).map(action => (
                            <Badge 
                              key={action.action_id} 
                              variant="outline" 
                              className={cn(
                                "text-xs",
                                action.scope === 'collection' ? "border-purple-300 text-purple-600" : "border-blue-300 text-blue-600"
                              )}
                            >
                              {action.scope === 'collection' ? <Layers className="w-3 h-3 mr-1" /> : <Target className="w-3 h-3 mr-1" />}
                              {action.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                )}
                
                {/* Preview Results - Two Model Display */}
                {previewResult && (
                  <div className="flex-1 overflow-hidden">
                    <Tabs defaultValue="model1" className="h-full flex flex-col">
                      <TabsList className="bg-transparent border-b rounded-none justify-start px-0">
                        <TabsTrigger value="model1" className="data-[state=active]:bg-blue-500/10">
                          <Cpu className="w-4 h-4 mr-2" />
                          Model 1: Catalog
                        </TabsTrigger>
                        <TabsTrigger value="model1response" className="data-[state=active]:bg-blue-500/10">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Model 1: Response
                        </TabsTrigger>
                        <TabsTrigger value="model2" className="data-[state=active]:bg-amber-500/10">
                          <Zap className="w-4 h-4 mr-2" />
                          Model 2: Input
                        </TabsTrigger>
                        <TabsTrigger value="model2response" className="data-[state=active]:bg-amber-500/10">
                          <Sparkles className="w-4 h-4 mr-2" />
                          Model 2: Response
                        </TabsTrigger>
                        <TabsTrigger value="actions" className="data-[state=active]:bg-purple-500/10">
                          <Target className="w-4 h-4 mr-2" />
                          Actions
                        </TabsTrigger>
                      </TabsList>
                      
                      <div className="flex-1 overflow-hidden pt-4">
                        {/* Model 1: Catalog Input */}
                        <TabsContent value="model1" className="h-full m-0">
                          <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                            <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                              <div className="flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-blue-500" />
                                <span className={cn("font-medium text-sm", colors.textPrimary)}>
                                  Model 1 (Orchestrator) - Catalog Input
                                </span>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {previewResult.model_1?.model || 'gpt-4o-mini'}
                              </Badge>
                            </div>
                            <ScrollArea className="h-[400px] p-4">
                              <div className="space-y-4">
                                <div>
                                  <h5 className={cn("font-medium text-sm mb-2 flex items-center gap-2", colors.textPrimary)}>
                                    <Database className="w-4 h-4" />
                                    Catalog Data (What Model 1 Sees):
                                  </h5>
                                  <pre className={cn("text-xs whitespace-pre-wrap font-mono p-3 rounded-lg", colors.bgTertiary, colors.textSecondary)}>
                                    {previewResult.model_1?.catalog 
                                      ? JSON.stringify(previewResult.model_1.catalog, null, 2) 
                                      : 'Catalog data not available'}
                                  </pre>
                                </div>
                                <div>
                                  <h5 className={cn("font-medium text-sm mb-2", colors.textPrimary)}>System Prompt:</h5>
                                  <pre className={cn("text-xs whitespace-pre-wrap font-mono p-3 rounded-lg", colors.bgTertiary, colors.textSecondary)}>
                                    {previewResult.model_1?.system_prompt || 'System prompt not available'}
                                  </pre>
                                </div>
                              </div>
                            </ScrollArea>
                          </Card>
                        </TabsContent>
                        
                        {/* Model 1: Response (Data Request + Crafted Prompt) */}
                        <TabsContent value="model1response" className="h-full m-0">
                          <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                            <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                              <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-blue-500" />
                                <span className={cn("font-medium text-sm", colors.textPrimary)}>
                                  Model 1 Response → Data Selection
                                </span>
                              </div>
                            </div>
                            <ScrollArea className="h-[400px] p-4">
                              <div className="space-y-4">
                                {/* Model 1 Reasoning */}
                                {previewResult.model_1?.output?.parsed?.reasoning && (
                                  <div className={cn("p-3 rounded-lg border-l-4 border-green-500", colors.bgTertiary)}>
                                    <h5 className={cn("font-medium text-sm mb-2", colors.textPrimary)}>
                                      Data Selection Reasoning:
                                    </h5>
                                    <p className={cn("text-sm", colors.textSecondary)}>
                                      {previewResult.model_1.output.parsed.reasoning}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Model 1 Data Request */}
                                <div className={cn("p-3 rounded-lg border-l-4 border-blue-500", colors.bgTertiary)}>
                                  <h5 className={cn("font-medium text-sm mb-2", colors.textPrimary)}>
                                    Data Request:
                                  </h5>
                                  <pre className={cn("text-xs whitespace-pre-wrap font-mono", colors.textSecondary)}>
                                    {previewResult.model_1?.output?.parsed?.data_request 
                                      ? JSON.stringify(previewResult.model_1.output.parsed.data_request, null, 2)
                                      : previewResult.model_1?.output?.raw_response || 'Response not available'}
                                  </pre>
                                </div>
                                
                                {/* Raw Response (collapsible) */}
                                {previewResult.model_1?.output?.raw_response && (
                                  <details className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                                    <summary className={cn("font-medium text-sm cursor-pointer", colors.textMuted)}>
                                      Raw LLM Response
                                    </summary>
                                    <pre className={cn("text-xs whitespace-pre-wrap font-mono mt-2", colors.textSecondary)}>
                                      {previewResult.model_1.output.raw_response}
                                    </pre>
                                  </details>
                                )}
                                
                                <div>
                                  <h5 className={cn("font-medium text-sm mb-2 flex items-center gap-2", colors.textPrimary)}>
                                    <Database className="w-4 h-4" />
                                    Data Fetched Based on Request:
                                  </h5>
                                  <pre className={cn("text-xs whitespace-pre-wrap font-mono p-3 rounded-lg", colors.bgTertiary, colors.textSecondary)}>
                                    {previewResult.data_fetched 
                                      ? JSON.stringify(previewResult.data_fetched, null, 2) 
                                      : 'No data fetched'}
                                  </pre>
                                </div>
                              </div>
                            </ScrollArea>
                          </Card>
                        </TabsContent>
                        
                        {/* Model 2: Input (Crafted Prompt + Data) */}
                        <TabsContent value="model2" className="h-full m-0">
                          <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                            <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-amber-500" />
                                <span className={cn("font-medium text-sm", colors.textPrimary)}>
                                  Model 2 (Executor) - Full Input
                                </span>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {previewResult.model_2?.model || 'gpt-4o'}
                              </Badge>
                            </div>
                            <ScrollArea className="h-[400px] p-4">
                              <div>
                                <h5 className={cn("font-medium text-sm mb-2", colors.textPrimary)}>
                                  Complete Prompt Sent to Executor:
                                </h5>
                                <pre className={cn("text-xs whitespace-pre-wrap font-mono p-3 rounded-lg", colors.bgTertiary, colors.textSecondary)}>
                                  {previewResult.model_2?.input_prompt || 'Input prompt not available'}
                                </pre>
                              </div>
                            </ScrollArea>
                          </Card>
                        </TabsContent>
                        
                        {/* Model 2: Response (Analysis) */}
                        <TabsContent value="model2response" className="h-full m-0">
                          <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                            <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <span className={cn("font-medium text-sm", colors.textPrimary)}>
                                  Model 2 (Executor) - Final Analysis
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={cn("text-xs", colors.textMuted)}>
                                  Click entity IDs to view details
                                </span>
                                {previewResult.token_usage && (
                                  <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30 text-xs">
                                    {previewResult.token_usage.total_tokens?.toLocaleString() || '?'} tokens
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <ScrollArea className="h-[400px] p-4">
                              <div className="space-y-4">
                                {/* AI Suggested Action */}
                                {previewResult.model_2?.response && (() => {
                                  const action = parseActionBlock(previewResult.model_2.response);
                                  if (action && action.type !== 'AWAIT_FEEDBACK') {
                                    return (
                                      <UnifiedActionReview
                                        action={action}
                                        productId={previewSelectedProduct}
                                        onActionComplete={(result) => {
                                          toast.success('Action completed successfully');
                                          // Optionally refresh preview
                                        }}
                                        onDismiss={() => {
                                          toast.info('Action dismissed');
                                        }}
                                      />
                                    );
                                  }
                                  return null;
                                })()}
                                
                                {/* Response Text */}
                                <div className={cn("whitespace-pre-wrap text-sm", colors.textSecondary)}>
                                  {previewResult.error ? (
                                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                                      <AlertTriangle className="w-4 h-4 inline mr-2" />
                                      {previewResult.error}
                                    </div>
                                  ) : previewResult.model_2?.response ? (
                                    <EntityLinkedText 
                                      text={removeActionBlock(previewResult.model_2.response)}
                                      onEntityClick={(type, id) => {
                                        const routes = {
                                          feature: `/products/${previewSelectedProduct}/features?highlight=${id}`,
                                          epic: `/products/${previewSelectedProduct}/epics?highlight=${id}`,
                                          goal: `/products/${previewSelectedProduct}/goals?highlight=${id}`,
                                          initiative: `/products/${previewSelectedProduct}/initiatives?highlight=${id}`,
                                          persona: `/products/${previewSelectedProduct}/personas?highlight=${id}`,
                                          release: `/products/${previewSelectedProduct}/releases?highlight=${id}`,
                                          product: `/products/${id}`
                                        };
                                        const route = routes[type];
                                        if (route) {
                                          window.open(route, '_blank');
                                          toast.info(`Opening ${type}: ${id}`);
                                        } else {
                                          toast.info(`${type}: ${id}`, { description: 'Entity ID copied to clipboard' });
                                          navigator.clipboard.writeText(id);
                                        }
                                      }}
                                    />
                                  ) : (
                                    'No response yet'
                                  )}
                                </div>
                              </div>
                            </ScrollArea>
                          </Card>
                        </TabsContent>
                        
                        <TabsContent value="actions" className="h-full m-0">
                          <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                            <div className={cn("px-4 py-2 border-b", colors.borderPrimary)}>
                              <div className="flex items-center gap-2">
                                <Target className="w-4 h-4 text-purple-500" />
                                <span className={cn("font-medium text-sm", colors.textPrimary)}>Enabled Actions</span>
                              </div>
                            </div>
                            <ScrollArea className="h-[400px] p-4">
                              <div className="space-y-3">
                                {editedPrompt.actions?.filter(a => a.is_enabled !== false).map(action => (
                                  <div key={action.action_id} className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                                    <div className="flex items-center gap-2 mb-1">
                                      {action.scope === 'collection' ? (
                                        <Layers className="w-4 h-4 text-purple-500" />
                                      ) : (
                                        <Target className="w-4 h-4 text-blue-500" />
                                      )}
                                      <span className={cn("font-medium", colors.textPrimary)}>{action.name}</span>
                                      <Badge variant="outline" className="text-xs">
                                        {action.scope === 'collection' ? 'Collection' : 'Per Item'}
                                      </Badge>
                                    </div>
                                    <p className={cn("text-sm", colors.textMuted)}>{action.description}</p>
                                    {action.trigger_phrases?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {action.trigger_phrases.map((phrase, i) => (
                                          <Badge key={i} variant="outline" className="text-xs">
                                            &quot;{phrase}&quot;
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )) || (
                                  <p className={cn("text-center py-8", colors.textMuted)}>No actions defined</p>
                                )}
                              </div>
                            </ScrollArea>
                          </Card>
                        </TabsContent>
                      </div>
                    </Tabs>
                  </div>
                )}
              </>
            ) : (
              /* Item Co-Pilot Preview - Original UI */
              <>
                {/* Item Selection Row */}
                <div className={cn("p-4 rounded-lg space-y-3", colors.bgSecondary)}>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Product Selection */}
                    <div>
                      <Label className={cn("text-xs mb-1 block", colors.textMuted)}>Product</Label>
                      <Select 
                        value={previewSelectedProduct} 
                        onValueChange={(v) => {
                          setPreviewSelectedProduct(v);
                          setPreviewSelectedItem('');
                          setPreviewItems([]);
                          
                          // Auto-select item type based on prompt's domains
                          const availableTypes = getAvailableItemTypes();
                          if (availableTypes.length === 1) {
                            // Single domain - auto-select and load items
                            const autoType = availableTypes[0].value;
                            setPreviewSelectedItemType(autoType);
                            if (v) {
                              loadPreviewItems(v, autoType);
                            }
                          } else if (availableTypes.length > 1 && previewSelectedItemType) {
                            // Multiple domains - keep current selection if valid
                            if (availableTypes.find(t => t.value === previewSelectedItemType)) {
                              loadPreviewItems(v, previewSelectedItemType);
                            }
                          }
                        }}
                      >
                        <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                          <SelectValue placeholder="Select product..." />
                        </SelectTrigger>
                        <SelectContent>
                          {previewProducts.map(p => (
                            <SelectItem key={p.product_id} value={p.product_id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Item Selection - Show item type in label if single domain */}
                    <div>
                      <Label className={cn("text-xs mb-1 block", colors.textMuted)}>
                        {getAvailableItemTypes().length === 1 
                          ? getAvailableItemTypes()[0].label 
                          : `Item (${getAvailableItemTypes().map(t => t.label).join('/')})`}
                      </Label>
                      {getAvailableItemTypes().length > 1 ? (
                        // Multiple domains - show type selector first
                        <div className="flex gap-2">
                          <Select 
                            value={previewSelectedItemType} 
                            onValueChange={(v) => {
                              setPreviewSelectedItemType(v);
                              setPreviewSelectedItem('');
                              if (previewSelectedProduct && v) {
                                loadPreviewItems(previewSelectedProduct, v);
                              }
                            }}
                            disabled={!previewSelectedProduct}
                          >
                            <SelectTrigger className={cn("w-[120px]", colors.bgInput, colors.borderPrimary)}>
                              <SelectValue placeholder="Type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableItemTypes().map(t => (
                                <SelectItem key={t.value} value={t.value}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select 
                            value={previewSelectedItem} 
                            onValueChange={setPreviewSelectedItem}
                            disabled={!previewSelectedItemType || loadingPreviewItems}
                          >
                            <SelectTrigger className={cn("flex-1", colors.bgInput, colors.borderPrimary)}>
                              <SelectValue placeholder={loadingPreviewItems ? "Loading..." : "Select..."} />
                            </SelectTrigger>
                            <SelectContent>
                              {previewItems.map(item => (
                                <SelectItem key={item.id} value={item.id}>
                                  {item.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        // Single domain - just show item selector
                        <Select 
                          value={previewSelectedItem} 
                          onValueChange={setPreviewSelectedItem}
                          disabled={!previewSelectedProduct || loadingPreviewItems}
                        >
                          <SelectTrigger className={cn(colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder={loadingPreviewItems ? "Loading..." : "Select item..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {previewItems.map(item => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  
                  
                  {/* Test Message */}
                  <div>
                    <Label className={cn("text-xs mb-1 block", colors.textMuted)}>Test Message (User Input)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        placeholder="Enter a test message..."
                        className={cn("flex-1", colors.bgInput, colors.borderPrimary)}
                      />
                      <Button 
                        onClick={handleFullPreview} 
                        disabled={previewLoading || !previewSelectedItem}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Run Preview
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Results */}
            {previewResult && (
              <div className="flex-1 overflow-hidden">
                {/* Token Usage Summary */}
                {previewResult.token_usage && !previewResult.token_usage.error && (
                  <div className={cn("mb-3 p-3 rounded-lg border flex items-center justify-between", colors.bgCard, colors.borderPrimary)}>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className={cn("text-xs font-medium", colors.textMuted)}>Input:</span>
                        <span className={cn("text-sm", colors.textPrimary)}>
                          {previewResult.token_usage.input_tokens.toLocaleString()} tokens
                        </span>
                        <span className={cn("text-xs", colors.textMuted)}>
                          (${previewResult.token_usage.input_cost.toFixed(4)})
                        </span>
                      </div>
                      <span className={colors.textMuted}>+</span>
                      <div className="flex items-center gap-2">
                        <span className={cn("text-xs font-medium", colors.textMuted)}>Output:</span>
                        <span className={cn("text-sm", colors.textPrimary)}>
                          {previewResult.token_usage.output_tokens.toLocaleString()} tokens
                        </span>
                        <span className={cn("text-xs", colors.textMuted)}>
                          (${previewResult.token_usage.output_cost.toFixed(4)})
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn("text-xs font-medium", colors.textMuted)}>Total Cost:</span>
                      <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/30">
                        ${previewResult.token_usage.total_cost.toFixed(4)}
                      </Badge>
                      <span className={cn("text-xs", colors.textMuted)}>
                        ({previewResult.token_usage.total_tokens.toLocaleString()} tokens)
                      </span>
                    </div>
                  </div>
                )}
                
                <Tabs defaultValue="prompt" className="h-full flex flex-col">
                  <TabsList className="bg-transparent border-b rounded-none justify-start px-0">
                    <TabsTrigger value="prompt" className="data-[state=active]:bg-blue-500/10">
                      <Code className="w-4 h-4 mr-2" />
                      Assembled Prompt
                    </TabsTrigger>
                    <TabsTrigger value="response" className="data-[state=active]:bg-blue-500/10">
                      <Sparkles className="w-4 h-4 mr-2" />
                      AI Response
                    </TabsTrigger>
                    <TabsTrigger value="context" className="data-[state=active]:bg-blue-500/10">
                      <Layers className="w-4 h-4 mr-2" />
                      Context Data
                    </TabsTrigger>
                  </TabsList>
                  
                  <div className="flex-1 overflow-hidden pt-4">
                    <TabsContent value="prompt" className="h-full m-0">
                      <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                        <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                          <div className="flex items-center gap-2">
                            <Code className="w-4 h-4 text-blue-500" />
                            <span className={cn("font-medium text-sm", colors.textPrimary)}>
                              Full Assembled Prompt
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {previewResult.token_usage && !previewResult.token_usage.error && (
                              <Badge variant="outline" className="text-xs">
                                {previewResult.token_usage.input_tokens.toLocaleString()} tokens · ${previewResult.token_usage.input_cost.toFixed(4)}
                              </Badge>
                            )}
                            <Badge variant="outline">
                              {previewResult.assembled_prompt?.length || 0} chars
                            </Badge>
                          </div>
                        </div>
                        <ScrollArea className="h-[400px] p-4">
                          <pre className={cn("text-xs whitespace-pre-wrap font-mono", colors.textSecondary)}>
                            {previewResult.assembled_prompt || 'No prompt assembled'}
                          </pre>
                        </ScrollArea>
                      </Card>
                    </TabsContent>
                    
                    <TabsContent value="response" className="h-full m-0">
                      <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                        <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            <span className={cn("font-medium text-sm", colors.textPrimary)}>
                              AI Response
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {previewResult.token_usage && !previewResult.token_usage.error && (
                              <Badge variant="outline" className="text-xs">
                                {previewResult.token_usage.output_tokens.toLocaleString()} tokens · ${previewResult.token_usage.output_cost.toFixed(4)}
                              </Badge>
                            )}
                            <Badge variant="outline">
                              {previewResult.prompt_config?.model}
                            </Badge>
                          </div>
                        </div>
                        <ScrollArea className="h-[400px] p-4">
                          {previewResult.error ? (
                            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                              <AlertTriangle className="w-4 h-4 inline mr-2" />
                              {previewResult.error}
                            </div>
                          ) : (
                            <pre className={cn("text-sm whitespace-pre-wrap", colors.textSecondary)}>
                              {previewResult.response || 'No response generated'}
                            </pre>
                          )}
                        </ScrollArea>
                      </Card>
                    </TabsContent>
                    
                    <TabsContent value="context" className="h-full m-0">
                      <Card className={cn("h-full overflow-hidden", colors.bgCard, colors.borderPrimary)}>
                        <div className={cn("px-4 py-2 border-b flex items-center justify-between", colors.borderPrimary)}>
                          <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-purple-500" />
                            <span className={cn("font-medium text-sm", colors.textPrimary)}>
                              Context Data Used
                            </span>
                          </div>
                          <Badge variant="outline">
                            {previewResult.context?.item_type} / {previewResult.context?.item_id}
                          </Badge>
                        </div>
                        <ScrollArea className="h-[400px] p-4">
                          <pre className={cn("text-xs whitespace-pre-wrap font-mono", colors.textSecondary)}>
                            {previewResult.context?.context_json || 'No context data'}
                          </pre>
                        </ScrollArea>
                      </Card>
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            )}
            
            {!previewResult && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Eye className={cn("w-16 h-16 mx-auto mb-4", colors.textMuted)} />
                  <h3 className={cn("font-medium", colors.textPrimary)}>Ready to Preview</h3>
                  <p className={cn("text-sm mt-1", colors.textMuted)}>
                    Select a product, item type, and item, then click &quot;Run Preview&quot;
                  </p>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Centralized Debug Logs Modal */}
      <Dialog open={showDebugModal} onOpenChange={(open) => {
        setShowDebugModal(open);
        if (!open) {
          setSelectedDebugLog(null);
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-amber-500" />
              Co-Pilot Debug Logging
            </DialogTitle>
            <DialogDescription>
              Configure debug logging scope and view captured AI interactions. Logs are retained for 72 hours.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={debugModalTab} onValueChange={setDebugModalTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="logs" className="gap-2">
                <Code className="w-4 h-4" />
                Logs ({debugLogsTotal})
              </TabsTrigger>
            </TabsList>
            
            {/* Settings Tab */}
            <TabsContent value="settings" className="flex-1 overflow-auto mt-4">
              <div className="space-y-6">
                {/* Master Toggle */}
                <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", debugSettings.enabled ? "bg-green-100 dark:bg-green-900/50" : "bg-gray-100 dark:bg-gray-800")}>
                        <Radio className={cn("w-5 h-5", debugSettings.enabled ? "text-green-600" : colors.textMuted)} />
                      </div>
                      <div>
                        <h4 className={cn("font-medium", colors.textPrimary)}>Debug Logging</h4>
                        <p className={cn("text-sm", colors.textMuted)}>
                          {debugSettings.enabled ? 'Actively capturing logs' : 'Logging disabled'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={debugSettings.enabled}
                      onCheckedChange={(v) => setDebugSettings({ ...debugSettings, enabled: v })}
                    />
                  </div>
                </Card>
                
                {/* Scope Selection */}
                <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                  <h4 className={cn("font-medium mb-4", colors.textPrimary)}>Logging Scope</h4>
                  <div className="space-y-4">
                    {/* Organization Selection */}
                    <div>
                      <label className={cn("text-sm font-medium mb-2 block", colors.textSecondary)}>Organization</label>
                      <Select
                        value={debugSettings.scope_organization_id || 'all'}
                        onValueChange={(v) => {
                          const newOrgId = v === 'all' ? null : v;
                          setDebugSettings({
                            ...debugSettings,
                            scope_type: newOrgId ? 'organization' : 'all',
                            scope_organization_id: newOrgId,
                            scope_product_id: null // Reset product when org changes
                          });
                          // Load products for this org
                          if (newOrgId) {
                            loadProductsForOrg(newOrgId);
                          } else {
                            setProducts([]);
                          }
                        }}
                      >
                        <SelectTrigger className={cn("w-full max-w-md", colors.bgInput)}>
                          <SelectValue placeholder="Select organization..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Organizations</SelectItem>
                          {organizations.map(org => (
                            <SelectItem key={org.organization_id} value={org.organization_id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Product Selection - Only shown when org is selected */}
                    {debugSettings.scope_organization_id && (
                      <div>
                        <label className={cn("text-sm font-medium mb-2 block", colors.textSecondary)}>Product</label>
                        <Select
                          value={debugSettings.scope_product_id || 'all'}
                          onValueChange={(v) => {
                            const newProductId = v === 'all' ? null : v;
                            setDebugSettings({
                              ...debugSettings,
                              scope_type: newProductId ? 'product' : 'organization',
                              scope_product_id: newProductId
                            });
                          }}
                        >
                          <SelectTrigger className={cn("w-full max-w-md", colors.bgInput)}>
                            <SelectValue placeholder="Select product..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Products in Organization</SelectItem>
                            {products.map(prod => (
                              <SelectItem key={prod.product_id} value={prod.product_id}>
                                {prod.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {products.length === 0 && (
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            No products found in this organization
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Scope Summary */}
                    <div className={cn("p-3 rounded-lg text-sm", colors.bgSecondary)}>
                      <span className={colors.textMuted}>Logging scope: </span>
                      <span className={cn("font-medium", colors.textPrimary)}>
                        {!debugSettings.scope_organization_id && 'All organizations and products'}
                        {debugSettings.scope_organization_id && !debugSettings.scope_product_id && (
                          <>All products in &ldquo;{organizations.find(o => o.organization_id === debugSettings.scope_organization_id)?.name}&rdquo;</>
                        )}
                        {debugSettings.scope_product_id && (
                          <>&ldquo;{products.find(p => p.product_id === debugSettings.scope_product_id)?.name}&rdquo; only</>
                        )}
                      </span>
                    </div>
                  </div>
                </Card>
                
                {/* Co-Pilot Selection */}
                <Card className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
                  <h4 className={cn("font-medium mb-4", colors.textPrimary)}>Co-Pilots to Log</h4>
                  <div className="space-y-4">
                    {/* Collection Scope */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn("text-sm font-medium", colors.textSecondary)}>Collection Scope</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAllCopilots('collection')}
                          className="text-xs"
                        >
                          {prompts.filter(p => p.category === 'collection').every(p => debugSettings.enabled_copilots?.includes(p.prompt_id)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {prompts.filter(p => p.category === 'collection').map(prompt => (
                          <label key={prompt.prompt_id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                            <input
                              type="checkbox"
                              checked={debugSettings.enabled_copilots?.includes(prompt.prompt_id) || false}
                              onChange={() => toggleCopilotEnabled(prompt.prompt_id)}
                              className="w-4 h-4 rounded"
                            />
                            <span className={cn("text-sm", colors.textPrimary)}>{prompt.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Single Entity */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn("text-sm font-medium", colors.textSecondary)}>Single Entity</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAllCopilots('item')}
                          className="text-xs"
                        >
                          {prompts.filter(p => p.category === 'item').every(p => debugSettings.enabled_copilots?.includes(p.prompt_id)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {prompts.filter(p => p.category === 'item').map(prompt => (
                          <label key={prompt.prompt_id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                            <input
                              type="checkbox"
                              checked={debugSettings.enabled_copilots?.includes(prompt.prompt_id) || false}
                              onChange={() => toggleCopilotEnabled(prompt.prompt_id)}
                              className="w-4 h-4 rounded"
                            />
                            <span className={cn("text-sm", colors.textPrimary)}>{prompt.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Workflows */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn("text-sm font-medium", colors.textSecondary)}>Workflows</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAllCopilots('workflow')}
                          className="text-xs"
                        >
                          {prompts.filter(p => p.category === 'workflow').every(p => debugSettings.enabled_copilots?.includes(p.prompt_id)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {prompts.filter(p => p.category === 'workflow').map(prompt => (
                          <label key={prompt.prompt_id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                            <input
                              type="checkbox"
                              checked={debugSettings.enabled_copilots?.includes(prompt.prompt_id) || false}
                              onChange={() => toggleCopilotEnabled(prompt.prompt_id)}
                              className="w-4 h-4 rounded"
                            />
                            <span className={cn("text-sm", colors.textPrimary)}>{prompt.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
                
                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowClearLogsConfirm(true)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All Logs
                  </Button>
                  
                  <Button onClick={() => saveDebugSettings(debugSettings)}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            {/* Logs Tab */}
            <TabsContent value="logs" className="flex-1 overflow-hidden mt-4 flex gap-4">
              {/* Log List */}
              <div className="w-2/5 flex flex-col border-r pr-4">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <Select
                    value={debugLogFilter.length === 0 ? 'all' : debugLogFilter.join(',')}
                    onValueChange={(v) => {
                      setDebugLogFilter(v === 'all' ? [] : [v]);
                      setTimeout(loadDebugLogs, 100);
                    }}
                  >
                    <SelectTrigger className={cn("w-48", colors.bgInput)}>
                      <SelectValue placeholder="Filter by Co-Pilot" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Co-Pilots</SelectItem>
                      {prompts.map(p => (
                        <SelectItem key={p.prompt_id} value={p.prompt_id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadDebugLogs}>
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportDebugLogs}>
                      <Download className="w-3 h-3 mr-1" />
                      Export
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowClearLogsConfirm(true)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      disabled={debugLogs.length === 0}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All
                    </Button>
                  </div>
                </div>
                
                <ScrollArea className="flex-1">
                  {debugLogsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : debugLogs.length === 0 ? (
                    <div className="text-center py-8">
                      <Bug className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
                      <p className={cn("text-sm", colors.textMuted)}>No debug logs</p>
                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                        Enable logging and interact with co-pilots to capture logs
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {debugLogs.map((log) => (
                        <div
                          key={log.log_id}
                          onClick={() => loadDebugLogDetail(log.log_id)}
                          className={cn(
                            "w-full text-left p-3 rounded-lg border transition-colors cursor-pointer relative group",
                            selectedDebugLog?.log_id === log.log_id
                              ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700"
                              : cn("hover:bg-gray-50 dark:hover:bg-gray-800/50", colors.borderPrimary)
                          )}
                        >
                          {/* Delete button - top right */}
                          <button
                            onClick={(e) => deleteSingleLog(log.log_id, e)}
                            className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
                            title="Delete log"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          
                          <p className={cn("text-sm font-medium mb-1 pr-6 line-clamp-1", colors.textPrimary)}>
                            {log.user_prompt_title || log.request?.user_message?.substring(0, 60) || 'No prompt'}
                          </p>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px]">
                              {log.copilot_type}
                            </Badge>
                            {log.copilot_version && (
                              <Badge variant="secondary" className="text-[10px]">
                                v{log.copilot_version}
                              </Badge>
                            )}
                            {log.response?.errors?.length > 0 && (
                              <Badge variant="destructive" className="text-[10px]">Error</Badge>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <p className={cn("text-[10px]", colors.textMuted)}>
                              {new Date(log.timestamp).toLocaleString()}
                            </p>
                            {log.response?.latency_ms && (
                              <p className={cn("text-[10px]", colors.textMuted)}>
                                {log.response.latency_ms.toFixed(0)}ms
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
              
              {/* Log Detail */}
              <div className="flex-1 min-w-0 overflow-hidden">
                {selectedDebugLog ? (
                  <Tabs defaultValue="request" className="h-full flex flex-col">
                    <TabsList className="w-full justify-start">
                      <TabsTrigger value="request" className="text-xs">Request</TabsTrigger>
                      <TabsTrigger value="response" className="text-xs">Response</TabsTrigger>
                      <TabsTrigger value="metadata" className="text-xs">Metadata</TabsTrigger>
                    </TabsList>
                    
                    <div className="flex-1 min-w-0 overflow-hidden mt-3">
                      <TabsContent value="request" className="h-full m-0">
                        <ScrollArea className="h-[55vh]">
                          <div className="space-y-4 pr-4">
                            {/* Check if this is a collection copilot (has model_1_raw_request) or item copilot */}
                            {selectedDebugLog.request?.model_1_raw_request ? (
                              <>
                                {/* Collection Co-Pilot: Two models */}
                                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 overflow-hidden">
                                  <h5 className="text-xs font-semibold mb-3 text-blue-600 dark:text-blue-400">
                                    Model 1 (Orchestrator) - Raw Request
                                  </h5>
                                  <div className="space-y-2">
                                    <div className="flex gap-2 text-xs">
                                      <Badge variant="outline">{selectedDebugLog.request.model_1_raw_request.provider}</Badge>
                                      <Badge variant="outline">{selectedDebugLog.request.model_1_raw_request.model}</Badge>
                                    </div>
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <p className={cn("text-xs font-medium", colors.textMuted)}>System Message:</p>
                                        <CopyButton text={selectedDebugLog.request.model_1_raw_request.system_message} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.model_1_raw_request.system_message}
                                      </pre>
                                    </div>
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <p className={cn("text-xs font-medium", colors.textMuted)}>User Message:</p>
                                        <CopyButton text={selectedDebugLog.request.model_1_raw_request.user_message} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.model_1_raw_request.user_message}
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
                                  <h5 className="text-xs font-semibold mb-3 text-purple-600 dark:text-purple-400">
                                    Model 2 (Executor) - Raw Request
                                  </h5>
                                  <div className="space-y-2">
                                    <div className="flex gap-2 text-xs">
                                      <Badge variant="outline">{selectedDebugLog.request.model_2_raw_request.provider}</Badge>
                                      <Badge variant="outline">{selectedDebugLog.request.model_2_raw_request.model}</Badge>
                                    </div>
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <p className={cn("text-xs font-medium", colors.textMuted)}>System Message:</p>
                                        <CopyButton text={selectedDebugLog.request.model_2_raw_request.system_message} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.model_2_raw_request.system_message}
                                      </pre>
                                    </div>
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <p className={cn("text-xs font-medium", colors.textMuted)}>User Message:</p>
                                        <CopyButton text={selectedDebugLog.request.model_2_raw_request.user_message} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.model_2_raw_request.user_message}
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Check for Gemini 3 separated params */}
                                {selectedDebugLog.request?.gemini3_params ? (
                                  <>
                                    {/* Gemini 3 Structured Output - Separated Parameters */}
                                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 overflow-hidden">
                                      <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                          🧠 System Instruction (Role/Task/Guidelines)
                                        </h5>
                                        <CopyButton text={selectedDebugLog.request.gemini3_params.system_instruction || ''} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.gemini3_params.system_instruction || 'N/A'}
                                      </pre>
                                    </div>
                                    
                                    <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
                                      <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                                          📊 Context Data (Entities/Product Info)
                                        </h5>
                                        <CopyButton text={selectedDebugLog.request.gemini3_params.context_data || ''} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.gemini3_params.context_data || 'N/A'}
                                      </pre>
                                    </div>
                                    
                                    <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 overflow-hidden">
                                      <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-xs font-semibold text-green-600 dark:text-green-400">
                                          💬 User Message
                                        </h5>
                                        <CopyButton text={selectedDebugLog.request.gemini3_params.user_message || ''} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[150px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {selectedDebugLog.request.gemini3_params.user_message || 'N/A'}
                                      </pre>
                                    </div>
                                    
                                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 overflow-hidden">
                                      <div className="flex items-center justify-between mb-3">
                                        <h5 className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                                          📐 Response JSON Schema
                                        </h5>
                                        <CopyButton text={JSON.stringify(selectedDebugLog.request.gemini3_params.response_json_schema, null, 2)} label="Copy" />
                                      </div>
                                      <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                        {JSON.stringify(selectedDebugLog.request.gemini3_params.response_json_schema, null, 2)}
                                      </pre>
                                    </div>
                                    
                                    {/* File Attachments Section - Shows file_parts passed to Gemini */}
                                    {selectedDebugLog.request?.gemini3_params?.file_parts && selectedDebugLog.request.gemini3_params.file_parts.length > 0 && (
                                      <div className="p-3 rounded-lg bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 overflow-hidden">
                                        <div className="flex items-center justify-between mb-2">
                                          <h5 className="text-xs font-semibold text-pink-600 dark:text-pink-400">
                                            📎 File Attachments ({selectedDebugLog.request.gemini3_params.file_parts.length})
                                          </h5>
                                          <CopyButton text={JSON.stringify(selectedDebugLog.request.gemini3_params.file_parts, null, 2)} label="Copy" />
                                        </div>
                                        <div className="space-y-2">
                                          {selectedDebugLog.request.gemini3_params.file_parts.map((fp, idx) => (
                                            <div key={idx} className={cn("p-2 rounded border", colors.borderPrimary, colors.bgSecondary)}>
                                              <div className="flex items-center gap-2 mb-1">
                                                <Badge variant="outline" className="text-[10px]">
                                                  {fp.mime_type || 'unknown'}
                                                </Badge>
                                                {fp.filename && (
                                                  <span className="text-xs font-medium truncate">{fp.filename}</span>
                                                )}
                                              </div>
                                              {fp.file_uri && (
                                                <a 
                                                  href={fp.file_uri} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="text-[10px] font-mono text-blue-600 dark:text-blue-400 hover:underline break-all block"
                                                >
                                                  {fp.file_uri.length > 100 ? `${fp.file_uri.substring(0, 100)}...` : fp.file_uri}
                                                </a>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                        <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto p-2 rounded mt-2", colors.bgSecondary)}>
                                          {JSON.stringify(selectedDebugLog.request.gemini3_params.file_parts, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                    
                                    {selectedDebugLog.request?.model_config && (
                                      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 overflow-hidden">
                                        <div className="flex items-center justify-between mb-2">
                                          <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                            ⚙️ Model Configuration
                                          </h5>
                                          <div className="flex gap-2">
                                            <Badge variant="outline">{selectedDebugLog.request.model_config.provider}</Badge>
                                            <Badge variant="outline">{selectedDebugLog.request.model_config.model}</Badge>
                                            <Badge variant="secondary">T: {selectedDebugLog.request.model_config.temperature}</Badge>
                                            <Badge variant="secondary">Think: {selectedDebugLog.request.model_config.thinking_level}</Badge>
                                          </div>
                                        </div>
                                        <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all p-2 rounded", colors.bgSecondary)}>
                                          {JSON.stringify(selectedDebugLog.request.model_config, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {/* Legacy format: Single combined system message */}
                                    <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 overflow-hidden">
                                      <h5 className="text-xs font-semibold mb-3 text-green-600 dark:text-green-400">
                                        Raw LLM Request
                                      </h5>
                                      <div className="space-y-2">
                                        <div className="flex gap-2 text-xs">
                                          <Badge variant="outline">{selectedDebugLog.request?.provider || 'gemini'}</Badge>
                                          <Badge variant="outline">{selectedDebugLog.request?.model || 'unknown'}</Badge>
                                          {selectedDebugLog.request?.session_id && (
                                            <Badge variant="secondary" className="text-[10px]">Session: {selectedDebugLog.request.session_id}</Badge>
                                          )}
                                        </div>
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <p className={cn("text-xs font-medium", colors.textMuted)}>System Message:</p>
                                            <CopyButton text={selectedDebugLog.request?.system_message || selectedDebugLog.request?.system_prompt || ''} label="Copy" />
                                          </div>
                                          <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                            {selectedDebugLog.request?.system_message || selectedDebugLog.request?.system_prompt || 'N/A'}
                                          </pre>
                                        </div>
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <p className={cn("text-xs font-medium", colors.textMuted)}>User Message:</p>
                                            <CopyButton text={selectedDebugLog.request?.user_message || ''} label="Copy" />
                                          </div>
                                          <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                            {selectedDebugLog.request?.user_message || 'N/A'}
                                          </pre>
                                        </div>
                                        {selectedDebugLog.request?.model_config && (
                                          <div>
                                            <div className="flex items-center justify-between mb-1">
                                              <p className={cn("text-xs font-medium", colors.textMuted)}>Model Config:</p>
                                              <CopyButton text={JSON.stringify(selectedDebugLog.request.model_config, null, 2)} label="Copy" />
                                            </div>
                                            <pre className={cn("text-xs font-mono whitespace-pre-wrap p-2 rounded", colors.bgSecondary)}>
                                              {JSON.stringify(selectedDebugLog.request.model_config, null, 2)}
                                            </pre>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </ScrollArea>
                      </TabsContent>
                      
                      <TabsContent value="response" className="h-full m-0">
                        <ScrollArea className="h-[55vh]">
                          <div className="space-y-4">
                            {/* Latency */}
                            {selectedDebugLog.response?.latency_ms && (
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  Latency: {selectedDebugLog.response.latency_ms.toFixed(0)}ms
                                </Badge>
                              </div>
                            )}
                            
                            {/* Errors */}
                            {selectedDebugLog.response?.errors?.length > 0 && (
                              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 overflow-hidden">
                                <h5 className="text-xs font-medium mb-2 text-red-600 dark:text-red-400">Errors</h5>
                                {selectedDebugLog.response.errors.map((err, i) => (
                                  <p key={i} className="text-sm text-red-600 dark:text-red-400">{err}</p>
                                ))}
                              </div>
                            )}
                            
                            {/* Check if this is a collection copilot */}
                            {selectedDebugLog.response?.model_1_raw_response ? (
                              <>
                                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 overflow-hidden">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                      Model 1 (Orchestrator) - Raw Response
                                    </h5>
                                    <CopyButton text={selectedDebugLog.response.model_1_raw_response} label="Copy" />
                                  </div>
                                  <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                    {selectedDebugLog.response.model_1_raw_response}
                                  </pre>
                                </div>
                                
                                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                                      Model 2 (Executor) - Raw Response
                                    </h5>
                                    <CopyButton text={selectedDebugLog.response.model_2_raw_response} label="Copy" />
                                  </div>
                                  <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                    {selectedDebugLog.response.model_2_raw_response}
                                  </pre>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Show error prominently if present */}
                                {selectedDebugLog.response?.error && (
                                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 overflow-hidden mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <h5 className="text-xs font-semibold text-red-600 dark:text-red-400">
                                        ⚠️ LLM Error
                                      </h5>
                                      <CopyButton text={selectedDebugLog.response?.error || ''} label="Copy" />
                                    </div>
                                    <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                      {selectedDebugLog.response?.error}
                                    </pre>
                                  </div>
                                )}
                                
                                <div className={cn("p-3 rounded-lg overflow-hidden", 
                                  selectedDebugLog.response?.error 
                                    ? "bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800"
                                    : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                                )}>
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className={cn("text-xs font-semibold", 
                                      selectedDebugLog.response?.error 
                                        ? "text-gray-600 dark:text-gray-400"
                                        : "text-green-600 dark:text-green-400"
                                    )}>
                                      Raw LLM Response
                                    </h5>
                                    <CopyButton text={selectedDebugLog.response?.raw_response || ''} label="Copy" />
                                  </div>
                                  <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all max-h-[500px] overflow-y-auto p-2 rounded", colors.bgSecondary)}>
                                    {selectedDebugLog.response?.raw_response || (selectedDebugLog.response?.error ? 'No response (see error above)' : 'No response')}
                                  </pre>
                                </div>
                                
                                {/* Timing Information */}
                                {(selectedDebugLog.request?.request_timestamp || selectedDebugLog.response?.response_timestamp) && (
                                  <div className={cn("mt-3 p-3 rounded-lg", colors.bgSecondary)}>
                                    <h5 className={cn("text-xs font-semibold mb-2", colors.textMuted)}>
                                      ⏱️ Timing
                                    </h5>
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                      <div>
                                        <span className={colors.textMuted}>Request Sent:</span>
                                        <p className={cn("font-mono", colors.textSecondary)}>
                                          {selectedDebugLog.request?.request_timestamp 
                                            ? new Date(selectedDebugLog.request.request_timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })
                                            : 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <span className={colors.textMuted}>Response Received:</span>
                                        <p className={cn("font-mono", colors.textSecondary)}>
                                          {selectedDebugLog.response?.response_timestamp 
                                            ? new Date(selectedDebugLog.response.response_timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })
                                            : 'N/A'}
                                        </p>
                                      </div>
                                      <div>
                                        <span className={colors.textMuted}>Delta:</span>
                                        <p className={cn("font-mono font-semibold", colors.textPrimary)}>
                                          {selectedDebugLog.request?.request_timestamp && selectedDebugLog.response?.response_timestamp
                                            ? `${((new Date(selectedDebugLog.response.response_timestamp) - new Date(selectedDebugLog.request.request_timestamp)) / 1000).toFixed(2)}s`
                                            : 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </ScrollArea>
                      </TabsContent>
                      
                      <TabsContent value="metadata" className="h-full m-0">
                        <ScrollArea className="h-[55vh]">
                          <div className="space-y-4">
                            <div className={cn("p-3 rounded-lg", colors.bgSecondary)}>
                              <h5 className={cn("text-xs font-medium mb-2", colors.textMuted)}>Log ID</h5>
                              <code className={cn("text-xs font-mono break-all", colors.textSecondary)}>
                                {selectedDebugLog.log_id}
                              </code>
                            </div>
                            
                            <div className={cn("p-3 rounded-lg", colors.bgSecondary)}>
                              <h5 className={cn("text-xs font-medium mb-2", colors.textMuted)}>Timestamp</h5>
                              <p className={cn("text-sm", colors.textSecondary)}>
                                {new Date(selectedDebugLog.timestamp).toLocaleString()}
                              </p>
                            </div>
                            
                            <div className={cn("p-3 rounded-lg", colors.bgSecondary)}>
                              <h5 className={cn("text-xs font-medium mb-2", colors.textMuted)}>Co-Pilot</h5>
                              <p className={cn("text-sm", colors.textSecondary)}>
                                {selectedDebugLog.copilot_type}
                                {selectedDebugLog.copilot_version && (
                                  <Badge variant="secondary" className="ml-2 text-[10px]">
                                    v{selectedDebugLog.copilot_version}
                                  </Badge>
                                )}
                              </p>
                            </div>
                            
                            <div className={cn("p-3 rounded-lg", colors.bgSecondary)}>
                              <h5 className={cn("text-xs font-medium mb-2", colors.textMuted)}>Session ID</h5>
                              <code className={cn("text-xs font-mono break-all", colors.textSecondary)}>
                                {selectedDebugLog.session_id}
                              </code>
                            </div>
                            
                            {selectedDebugLog.metadata && (
                              <div className={cn("p-3 rounded-lg overflow-hidden", colors.bgSecondary)}>
                                <h5 className={cn("text-xs font-medium mb-3", colors.textMuted)}>Metadata</h5>
                                
                                {/* Resolved Names Section */}
                                <div className="space-y-2 mb-4">
                                  {selectedDebugLog.metadata.user_id && (
                                    <div className="flex items-center gap-2">
                                      <span className={cn("text-xs font-medium w-24", colors.textMuted)}>User:</span>
                                      <span className={cn("text-xs", colors.text)}>
                                        {resolvedMetadata[selectedDebugLog.metadata.user_id] || 'Loading...'}
                                      </span>
                                    </div>
                                  )}
                                  {selectedDebugLog.metadata.organization_id && (
                                    <div className="flex items-center gap-2">
                                      <span className={cn("text-xs font-medium w-24", colors.textMuted)}>Organization:</span>
                                      <span className={cn("text-xs", colors.text)}>
                                        {resolvedMetadata[selectedDebugLog.metadata.organization_id] || 'Loading...'}
                                      </span>
                                    </div>
                                  )}
                                  {selectedDebugLog.metadata.product_id && (
                                    <div className="flex items-center gap-2">
                                      <span className={cn("text-xs font-medium w-24", colors.textMuted)}>Product:</span>
                                      <span className={cn("text-xs", colors.text)}>
                                        {resolvedMetadata[selectedDebugLog.metadata.product_id] || 'Loading...'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Raw Metadata */}
                                <details className="cursor-pointer">
                                  <summary className={cn("text-xs font-medium mb-2", colors.textMuted)}>Raw IDs</summary>
                                  <pre className={cn("text-xs font-mono whitespace-pre-wrap break-all mt-2", colors.textSecondary)}>
                                    {JSON.stringify(selectedDebugLog.metadata, null, 2)}
                                  </pre>
                                </details>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </TabsContent>
                    </div>
                  </Tabs>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Eye className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
                      <p className={cn("text-sm", colors.textMuted)}>
                        Select a log entry to view details
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Clear Logs Confirmation Dialog - Outside Tabs so it works from any tab */}
          <Dialog open={showClearLogsConfirm} onOpenChange={setShowClearLogsConfirm}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Clear All Debug Logs?
                </DialogTitle>
                <DialogDescription>
                  This will permanently delete all {debugLogsTotal} debug log{debugLogsTotal !== 1 ? 's' : ''}. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowClearLogsConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={clearAllDebugLogs}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Logs
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </DialogContent>
      </Dialog>
      
    </div>
  );
};

export default AIPromptsPage;
